import {delete_, get, post, post_response, put} from "../../../helper/request";
import {takeScreenshot} from "./screenshot_utils";
import {
	getMaxViewZoom,
	getNativeTopZoom,
	getResolutions,
	getResolutionsForMicron,
	getZoomLevels,
	getZoomScaleForSlide,
} from "./map_utils";
import {
	apply as applyTransform,
	compose as composeTransform,
	create as createTransform,
	makeInverse,
} from "ol/transform";
import {componentIds, FileType, ScreenshotExtensions} from "../../../utils/const";
import {addCaseFile} from "../../../redux_actions/case.action";
import {store} from "../../../helper/store";
import {getTissueRaster, snapFeaturesToTissue} from "./ai_contour_snap";

export const AI_TRIGGER_RE = /^\s*@ai\b/i;

export const looksLikeAiTrigger = (text) => AI_TRIGGER_RE.test(text || "");

export const stripAiTrigger = (text) => (text || "").replace(/^\s*@ai\b[:\s]*/i, "").trim();

export const looksLikeViewportIntent = (text) => {
	const t = (text || "").trim().toLowerCase().replace(/\s+/g, " ");
	// Tolerate common typos like "what ami looking at"
	const compact = t.replace(/\s+/g, "");
	const phrases = [
		"what am i looking at",
		"what am i seeing",
		"describe this view",
		"describe the current view",
		"what's on screen",
		"whats on screen",
		"what is on screen",
		"this region",
		"this area",
		"this field",
		"this image",
		"this patch",
		"blue patch",
	];
	if (phrases.some((p) => t.includes(p))) return true;
	if (/\b(looking at|on screen|what('s|s| is| does) this)\b/.test(t)) return true;
	const compactPhrases = [
		"whatamilookingat",
		"whatamiseeing",
		"describethisview",
		"describethecurrentview",
		"whatsonscreen",
		"whatisonscreen",
		"imlookingat",
		"i'mlookingat",
	];
	return compactPhrases.some((p) => compact.includes(p));
};

/** User is asking for something drawn on the slide (needs current pixels + ai-geojson). */
export const looksLikeAnnotateIntent = (text) => {
	const t = (text || "").toLowerCase();
	return /\b(annot\w*|outline\w*|mark\w*|draw\w*|circle\w*|trace\w*|highlight\w*|borders?|boundar\w*|contour\w*|segment\w*|point (out|to)|show me where|where is|where are|measure\w*|count\w*|label\w*|box\w*)\b/.test(t);
};

export const isAiUnavailableMessage = (msg) => {
	if (!msg || msg.role !== "assistant") return false;
	if (msg.failed) return true;
	const c = (msg.content || "").trim();
	return (
		c.startsWith("AI unavailable:") ||
		/^Gemini API error\b/i.test(c) ||
		/^xAI (request|call) failed\b/i.test(c) ||
		/^Cursor (API error|call failed|agent)\b/i.test(c)
	);
};

export const fetchSlideAiModels = () => get("/api/slide_ai/models/");

const SLIDE_AI_PREFS_KEY = "morphle.slideAi.lastModel";

/** Max auto follow-up rounds and captures per round (hard caps). */
export const MAX_AUTO_VIEW_DEPTH = 3;
export const MAX_VIEW_REQUESTS_PER_ROUND = 3;

/** Clamp auto-capture depth: 0 = off, 1..MAX = follow-up rounds. */
export const clampAutoViewDepthMax = (n) => {
	const v = Math.round(Number(n));
	if (!Number.isFinite(v)) return 0;
	return Math.min(MAX_AUTO_VIEW_DEPTH, Math.max(0, v));
};

/**
 * Resolve stored depth. Depth 0 = off (replaces the old checkbox).
 * Migrates legacy auto_capture_views: false → 0; true + missing depth → 2.
 */
const resolveAutoViewDepthMax = (parsed) => {
	const depth = Number(parsed?.auto_view_depth_max);
	if (Number.isFinite(depth)) {
		const clamped = clampAutoViewDepthMax(depth);
		// Legacy: depth was always ≥1 while checkbox could be off.
		if (parsed.auto_capture_views === false && clamped > 0 && depth >= 1) {
			return 0;
		}
		return clamped;
	}
	if (parsed?.auto_capture_views === false) return 0;
	if (parsed?.auto_capture_views === true) return 2;
	return 0;
};

export const readLocalSlideAiPrefs = () => {
	try {
		const raw = localStorage.getItem(SLIDE_AI_PREFS_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		const autoViewDepthMax = resolveAutoViewDepthMax(parsed);
		return {
			last_provider: parsed.last_provider || parsed.provider || null,
			last_model_id: parsed.last_model_id || parsed.model_id || null,
			auto_view_depth_max: autoViewDepthMax,
			// Kept for older readers; prefer auto_view_depth_max.
			auto_capture_views: autoViewDepthMax > 0,
		};
	} catch (e) {
		return {};
	}
};

export const writeLocalSlideAiPrefs = ({
	provider, modelId, autoCaptureViews, autoViewDepthMax,
} = {}) => {
	try {
		const cur = readLocalSlideAiPrefs();
		let depth;
		if (autoViewDepthMax != null) {
			depth = clampAutoViewDepthMax(autoViewDepthMax);
		} else if (autoCaptureViews != null) {
			// Legacy writers: checkbox off → 0; on → keep current depth or 2.
			depth = autoCaptureViews
				? (cur.auto_view_depth_max > 0 ? cur.auto_view_depth_max : 2)
				: 0;
		} else {
			depth = clampAutoViewDepthMax(cur.auto_view_depth_max);
		}
		const next = {
			last_provider: provider != null ? provider : cur.last_provider,
			last_model_id: modelId != null ? modelId : cur.last_model_id,
			auto_view_depth_max: depth,
			auto_capture_views: depth > 0,
			updated_at: new Date().toISOString(),
		};
		localStorage.setItem(SLIDE_AI_PREFS_KEY, JSON.stringify(next));
		return next;
	} catch (e) {
		return {};
	}
};

export const fetchSlideAiPrefs = () => get("/api/slide_ai/prefs/");

export const saveSlideAiPrefs = ({provider, modelId, last_provider, last_model_id} = {}) => {
	const body = {
		last_provider: last_provider || provider,
		last_model_id: last_model_id != null ? last_model_id : modelId,
	};
	writeLocalSlideAiPrefs({provider: body.last_provider, modelId: body.last_model_id});
	return put("/api/slide_ai/prefs/", body);
};

export const fetchSlideAiSystemPrompt = () => get("/api/slide_ai/system_prompt/");

export const saveSlideAiSystemPrompt = (prompt) =>
	put("/api/slide_ai/system_prompt/", {prompt});

export const resetSlideAiSystemPrompt = () =>
	delete_("/api/slide_ai/system_prompt/");

export const fetchSlideAiChat = (slideId, chatId = null, {includeArchived = false} = {}) => {
	const q = new URLSearchParams({slide_id: String(slideId)});
	if (chatId) q.set("chat_id", String(chatId));
	if (includeArchived) q.set("include_archived", "1");
	return get(`/api/slide_ai/chat/?${q.toString()}`);
};

export const fetchSlideAiChats = ({q = "", limit = 100, includeArchived = false} = {}) => {
	const params = new URLSearchParams({
		q: q || "",
		limit: String(limit),
	});
	if (includeArchived) params.set("include_archived", "1");
	return get(`/api/slide_ai/chats/?${params.toString()}`);
};

export const fetchSlideAiChatSessions = (slideId, {includeArchived = false} = {}) => {
	const params = new URLSearchParams({slide_id: String(slideId)});
	if (includeArchived) params.set("include_archived", "1");
	return get(`/api/slide_ai/chat/sessions/?${params.toString()}`);
};

export const createSlideAiChat = (slideId, title = null, {includeArchived = false} = {}) =>
	post("/api/slide_ai/chat/sessions/", {
		slide_id: slideId,
		action: "new",
		title,
		...(includeArchived ? {include_archived: true} : {}),
	});

export const switchSlideAiChat = (slideId, chatId, {includeArchived = false} = {}) =>
	post("/api/slide_ai/chat/sessions/", {
		slide_id: slideId,
		action: "switch",
		chat_id: chatId,
		...(includeArchived ? {include_archived: true} : {}),
	});

export const renameSlideAiChat = (slideId, chatId, title, {includeArchived = false} = {}) =>
	post("/api/slide_ai/chat/sessions/", {
		slide_id: slideId,
		action: "rename",
		chat_id: chatId,
		title,
		...(includeArchived ? {include_archived: true} : {}),
	});

/** Soft-delete: hide from normal UI; session JSON kept on disk. */
export const linkSlideAiChatToCase = (slideId, chatId, link, {includeArchived = false} = {}) =>
	post("/api/slide_ai/chat/sessions/", {
		slide_id: slideId,
		action: link ? "link_case" : "unlink_case",
		chat_id: chatId,
		...(includeArchived ? {include_archived: true} : {}),
	});

const PENDING_HANDOFF_KEY = "morphle.slideAi.pendingHandoff";
const HANDOFF_TTL_MS = 2 * 60 * 1000;

/** One cross-part handoff at a time (newer overwrites older); survives the same-tab navigation. */
export const writePendingHandoff = (handoff) => {
	try {
		sessionStorage.setItem(PENDING_HANDOFF_KEY, JSON.stringify({
			...handoff,
			expires_at: Date.now() + HANDOFF_TTL_MS,
		}));
		return true;
	} catch (e) {
		return false;
	}
};

export const readPendingHandoff = () => {
	try {
		const h = JSON.parse(sessionStorage.getItem(PENDING_HANDOFF_KEY) || "null");
		if (!h || !(Number(h.expires_at) > Date.now())) {
			sessionStorage.removeItem(PENDING_HANDOFF_KEY);
			return null;
		}
		return h;
	} catch (e) {
		return null;
	}
};

export const clearPendingHandoff = () => {
	try { sessionStorage.removeItem(PENDING_HANDOFF_KEY); } catch (e) { /* ignore */ }
};

export const archiveSlideAiChat = (slideId, chatId, {includeArchived = false} = {}) =>
	post("/api/slide_ai/chat/sessions/", {
		slide_id: slideId,
		action: "archive",
		chat_id: chatId,
		...(includeArchived ? {include_archived: true} : {}),
	});

export const fetchCursorStatus = (refresh = false) =>
	get(refresh ? "/api/slide_ai/cursor/status/?refresh=1" : "/api/slide_ai/cursor/status/");

export const startCursorConnect = () => post("/api/slide_ai/cursor/connect/start/", {});

export const fetchCursorConnectStatus = (loginId) =>
	get(`/api/slide_ai/cursor/connect/status/?login_id=${encodeURIComponent(loginId || "")}`);

export const cancelCursorConnect = () => post("/api/slide_ai/cursor/connect/cancel/", {});

export const disconnectCursor = () => post("/api/slide_ai/cursor/disconnect/", {});

export const fetchCursorModels = (refresh = false) =>
	get(`/api/slide_ai/cursor/models/?refresh=${refresh ? "1" : "0"}`);

export const saveCursorPrefs = (prefs) => put("/api/slide_ai/cursor/prefs/", prefs);

export const fetchSlideAiJob = (jobId, slideId) =>
	get(`/api/slide_ai/jobs/${jobId}/?slide_id=${encodeURIComponent(slideId || "")}`);

export const cancelSlideAiJob = (jobId) => post(`/api/slide_ai/jobs/${jobId}/cancel/`, {});

export const askAnnotationAi = ({annotationId, question, rawComment, provider, modelId}) =>
	post("/api/slide_ai/annotation_ask/", {
		annotation_id: annotationId,
		question,
		raw_comment: rawComment,
		provider,
		model_id: modelId,
	});

/**
 * Capture viewport (+ optional /api/files/ upload). Resolves with blob + preview URL
 * so the UI can show the image before the chat POST starts.
 */
const _blobImageSize = (blob) => new Promise((resolve) => {
	if (!blob) {
		resolve(null);
		return;
	}
	if (typeof createImageBitmap === "function") {
		createImageBitmap(blob)
			.then((bmp) => {
				const size = [bmp.width, bmp.height];
				try { bmp.close(); } catch (e) { /* ignore */ }
				resolve(size);
			})
			.catch(() => resolve(null));
		return;
	}
	const url = URL.createObjectURL(blob);
	const img = new Image();
	img.onload = () => {
		const size = [img.naturalWidth, img.naturalHeight];
		URL.revokeObjectURL(url);
		resolve(size);
	};
	img.onerror = () => {
		URL.revokeObjectURL(url);
		resolve(null);
	};
	img.src = url;
});

/**
 * Scale bar + current MPP + all discrete zoom MPPs for this slide (Morpho Chat text context).
 * MPP = µm per screenshot/CSS pixel via OL projection metersPerUnit.
 */
export const readViewportScaleContext = ({slide, slidemap, mapState} = {}) => {
	const scaleEl =
		document.querySelector(`#${componentIds.scaleLineControl} .ol-scale-line-inner`)
		|| document.querySelector(".ol-scale-line-inner");
	const scale_bar = (scaleEl?.textContent || "").trim() || null;
	const slide_mpp = Number(slide?.uperpixel);
	let mpp = null;
	let currentZ = mapState?.z;
	try {
		const view = slidemap?.getView?.();
		const res = view?.getResolution?.();
		const mpu = view?.getProjection?.()?.getMetersPerUnit?.();
		if (Number.isFinite(res) && Number.isFinite(mpu) && mpu > 0) {
			mpp = res * mpu * 1e6; // meters → µm
		} else if (Number.isFinite(res) && Number.isFinite(slide_mpp) && slide_mpp > 0) {
			mpp = res * slide_mpp; // gamma pixel map units
		}
		const liveZ = view?.getZoom?.();
		if (Number.isFinite(liveZ)) currentZ = Math.round(liveZ);
	} catch (e) { /* ignore */ }
	if (mpp == null && Number.isFinite(slide_mpp) && slide_mpp > 0) {
		mpp = slide_mpp;
	}

	const available_mpp = readReachableZooms({slide, slidemap}).map((e) => (
		currentZ != null && e.z === currentZ ? {...e, is_current: true} : e
	));
	// Finest reachable MPP is the last ladder step (highest z / lowest µm/px).
	const finest = available_mpp.length
		? available_mpp[available_mpp.length - 1].mpp_um_per_px
		: (Number.isFinite(slide_mpp) && slide_mpp > 0 ? slide_mpp : null);

	return {
		scale_bar,
		mpp_um_per_px: mpp != null && Number.isFinite(mpp) ? Number(Number(mpp).toPrecision(6)) : null,
		slide_mpp_um_per_px: Number.isFinite(slide_mpp) && slide_mpp > 0 ? slide_mpp : null,
		finest_mpp_um_per_px: finest,
		current_z: currentZ != null ? currentZ : null,
		available_mpp_um_per_px: available_mpp.map((e) => e.mpp_um_per_px),
		available_zooms: available_mpp,
	};
};

/** Max zoom levels a single auto-capture round may step up from its source image. */
export const MAX_ZOOM_STEP_PER_ROUND = 2;

/**
 * Integer zooms the live view can actually reach (minZoom..maxZoom, digital zoom state,
 * zoomRatio) with their µm/px. Falls back to the slide ladder minus the digital level.
 */
export const readReachableZooms = ({slide, slidemap} = {}) => {
	const slide_mpp = Number(slide?.uperpixel);
	const zLevels = getZoomLevels(slide);
	const optical = zLevels.length ? getZoomScaleForSlide(slide, zLevels) : [];
	const nativeTop = zLevels.length ? getNativeTopZoom(slide, zLevels) : null;
	const out = [];
	try {
		const view = slidemap?.getView?.();
		const mpu = view?.getProjection?.()?.getMetersPerUnit?.();
		if (view?.getResolutionForZoom && view.getMinZoom && view.getMaxZoom) {
			const zMin = Math.max(0, Math.ceil(view.getMinZoom()));
			const zMax = Math.floor(view.getMaxZoom());
			for (let z = zMin; z <= zMax; z += 1) {
				const res = view.getResolutionForZoom(z);
				let levelMpp = null;
				if (Number.isFinite(res) && Number.isFinite(mpu) && mpu > 0) levelMpp = res * mpu * 1e6;
				else if (Number.isFinite(res) && slide_mpp > 0) levelMpp = res * slide_mpp;
				if (!(levelMpp > 0)) continue;
				const entry = {
					z,
					mpp_um_per_px: Number(Number(levelMpp).toPrecision(6)),
					optical_x: optical[z] != null ? optical[z] : null,
				};
				if (nativeTop != null && z > nativeTop) entry.digital = true;
				out.push(entry);
			}
		}
	} catch (e) { /* fall back below */ }
	if (out.length || !(slide_mpp > 0) || !zLevels.length) return out;
	const ladder = getResolutionsForMicron(zLevels, slide_mpp);
	const zMax = getMaxViewZoom(slide, zLevels, false);
	return ladder.slice(0, zMax + 1).map((levelMpp, z) => ({
		z,
		mpp_um_per_px: Number(Number(levelMpp).toPrecision(6)),
		optical_x: optical[z] != null ? optical[z] : null,
	}));
};

/** Live view center / zoom / rotation / resolution (the truth, unlike redux map state). */
export const readLiveMapState = (slidemap) => {
	try {
		const view = slidemap?.getView?.();
		const c = view?.getCenter?.();
		const res = view?.getResolution?.();
		if (!c || !Number.isFinite(c[0]) || !Number.isFinite(c[1]) || !(res > 0)) return null;
		return {
			x: c[0],
			y: c[1],
			z: view.getZoom(),
			r: view.getRotation() || 0,
			resolution: res,
		};
	} catch (e) {
		return null;
	}
};

/**
 * Capture target that matches OL extent used for AI geo conversion.
 * Prefer `.ol-viewport` (same box as getSize/calculateExtent), not outer chrome.
 */
export const resolveAiCaptureElement = (slidemap, activeMapId) => {
	try {
		const viewport = slidemap?.getViewport?.();
		if (viewport) return viewport;
		const fromMap = slidemap?.getTargetElement?.();
		if (fromMap) {
			const inner = fromMap.querySelector?.(".ol-viewport");
			if (inner) return inner;
			return fromMap;
		}
	} catch (e) { /* ignore */ }
	if (activeMapId != null) {
		const byId = document.getElementById(`slide-map-${activeMapId}`);
		if (byId) {
			const inner = byId.querySelector?.(".ol-viewport");
			if (inner) return inner;
			return byId;
		}
	}
	return document.getElementById(componentIds.slideMaps);
};

/** Read [minX,minY,maxX,maxY] for the current map FOV (same size as getSize). */
export const readMapExtent = (slidemap) => {
	try {
		const map = slidemap;
		if (!map?.getView || !map?.getSize) return null;
		const size = map.getSize();
		if (!size || size.length < 2 || !(size[0] > 0) || !(size[1] > 0)) return null;
		const ext = map.getView().calculateExtent(size);
		if (!ext || ext.length < 4) return null;
		return [Number(ext[0]), Number(ext[1]), Number(ext[2]), Number(ext[3])];
	} catch (e) {
		return null;
	}
};

/** CSS pixel size of the map viewport (same space as calculateExtent). */
export const readMapCssSize = (slidemap) => {
	try {
		const size = slidemap?.getSize?.();
		if (size && size.length >= 2 && size[0] > 0 && size[1] > 0) {
			return [Number(size[0]), Number(size[1])];
		}
	} catch (e) { /* ignore */ }
	return null;
};

/**
 * Part of the map canvas the user can actually see (side panels such as Morpho Chat
 * overlay the map). Returns [x0, y0, x1, y1] in canvas px, or null when fully visible.
 */
export const readVisibleCanvasBox = (slidemap, {cols = 32, rows = 16, minVisible = 0.6} = {}) => {
	try {
		const vp = slidemap?.getViewport?.();
		const size = slidemap?.getSize?.();
		if (!vp || !size || typeof document === "undefined") return null;
		const rect = vp.getBoundingClientRect();
		if (!(rect.width > 0 && rect.height > 0)) return null;
		const vw = window.innerWidth || rect.right;
		const vh = window.innerHeight || rect.bottom;
		const seen = (x, y) => {
			if (x < 0 || y < 0 || x >= vw || y >= vh) return false;
			const el = document.elementFromPoint(x, y);
			return Boolean(el && vp.contains(el));
		};
		const colVis = [];
		const rowVis = new Array(rows).fill(0);
		for (let c = 0; c < cols; c += 1) {
			let n = 0;
			const x = rect.left + ((c + 0.5) / cols) * rect.width;
			for (let r = 0; r < rows; r += 1) {
				const y = rect.top + ((r + 0.5) / rows) * rect.height;
				if (seen(x, y)) {
					n += 1;
					rowVis[r] += 1;
				}
			}
			colVis.push(n / rows);
		}
		const span = (vis, total) => {
			let a = 0;
			while (a < total && vis[a] < minVisible) a += 1;
			let b = total - 1;
			while (b > a && vis[b] < minVisible) b -= 1;
			return a < total ? [a, b + 1] : null;
		};
		const cs = span(colVis, cols);
		const rs = span(rowVis.map((n) => n / cols), rows);
		if (!cs || !rs) return null;
		if (cs[0] === 0 && cs[1] === cols && rs[0] === 0 && rs[1] === rows) return null;
		const sx = size[0] / cols;
		const sy = size[1] / rows;
		return [Math.round(cs[0] * sx), Math.round(rs[0] * sy), Math.round(cs[1] * sx), Math.round(rs[1] * sy)];
	} catch (e) {
		return null;
	}
};

/** Full slide bounds in viewer coords (tissue beyond the current crop). */
export const readSlideBounds = (slide, slidemap) => {
	try {
		const ext = slidemap?.getView?.()?.getProjection?.()?.getExtent?.();
		if (ext && ext.length >= 4) {
			return [Number(ext[0]), Number(ext[1]), Number(ext[2]), Number(ext[3])];
		}
	} catch (e) { /* ignore */ }
	const up = Number(slide?.uperpixel) || 1;
	const tw = Number(slide?.tile_width);
	const th = Number(slide?.tile_height);
	const xf = Number(slide?.x_fields);
	const yf = Number(slide?.y_fields);
	if ([tw, th, xf, yf].every((n) => Number.isFinite(n) && n > 0)) {
		// Gamma pixel maps use pixel units; micron maps scale by uperpixel elsewhere.
		const w = tw * xf;
		const h = th * yf;
		return [0, 0, w, h];
	}
	return null;
};

/**
 * Hide annotation layers for an AI capture: the model must see tissue, not earlier
 * outlines/labels (it copies or mistakes them for structures). Returns a restore fn.
 */
const _hideAnnotationLayersForCapture = (slidemap) => {
	const hidden = [];
	try {
		const walk = (layers) => (layers?.getArray?.() || []).forEach((l) => {
			if (l?.getLayers) {
				walk(l.getLayers());
				return;
			}
			const isAnno = typeof l?.getSource?.()?.getFeatures === "function"
				&& (l.get?.("drew") !== undefined || l.get?.("drawer") != null || (l.getZIndex?.() || 0) >= 1000000);
			if (isAnno && l.getVisible?.()) {
				l.setVisible(false);
				hidden.push(l);
			}
		});
		walk(slidemap?.getLayers?.());
		if (hidden.length) slidemap.renderSync?.();
	} catch (e) { /* capture anyway */ }
	let restored = false;
	const restore = () => {
		if (restored) return;
		restored = true;
		hidden.forEach((l) => l.setVisible(true));
		if (hidden.length) slidemap?.render?.();
	};
	// Never leave annotations hidden if the capture callback does not fire.
	if (hidden.length) setTimeout(restore, 20000);
	return restore;
};

export const captureAiViewportScreenshot = ({
	slide,
	slidemap,
	activeMapId,
	mapState,
	displaySetting,
	extension = ScreenshotExtensions.JPEG,
}) => new Promise((resolve) => {
	// Store what the map actually shows; redux map state lags behind moveend.
	const live = readLiveMapState(slidemap);
	const capturedMapState = live
		? {...(mapState || {}), x: live.x, y: live.y, z: live.z, r: live.r, resolution: live.resolution}
		: (mapState || {});
	// Read scale text before hiding the scale line for a clean capture.
	const scaleContext = readViewportScaleContext({slide, slidemap, mapState: capturedMapState});
	const captureEl = resolveAiCaptureElement(slidemap, activeMapId);
	const viewportCssSize = readMapCssSize(slidemap);
	const slideBounds = readSlideBounds(slide, slidemap);
	// Extent at capture time — must match the FOV of the bitmap we send the model.
	const extentAtCapture = readMapExtent(slidemap);
	const restoreAnnotations = _hideAnnotationLayersForCapture(slidemap);
	takeScreenshot(
		captureEl,
		extension,
		displaySetting,
		(blob) => {
			restoreAnnotations();
			const extent = readMapExtent(slidemap) || extentAtCapture;
			const cssSize = readMapCssSize(slidemap) || viewportCssSize;
			if (!blob) {
				resolve({
					blob: null, fileId: null, previewUrl: null, imageSize: null,
					scaleContext, viewportCssSize: cssSize, slideBounds, extent, mapState: capturedMapState,
				});
				return;
			}
			const previewUrl = URL.createObjectURL(blob);
			const finish = (fileId, urlSafe, imageSize, outBlob) => resolve({
				blob: outBlob || blob,
				fileId: fileId || null,
				previewUrl,
				urlSafe: urlSafe || null,
				imageSize: imageSize || null,
				scaleContext,
				viewportCssSize: cssSize,
				slideBounds,
				extent,
				mapState: capturedMapState,
			});
			const formData = new FormData();
			formData.append("file", blob, `ai_viewport${extension}`);
			if (slide?.case) formData.append("case", slide.case);
			formData.append("slide", slide.id);
			formData.append("meta", JSON.stringify({
				mapState: capturedMapState,
				scaleContext,
				viewportCssSize: cssSize,
				slideBounds,
				extent,
			}));
			formData.append("type", FileType.SCREENSHOT);
			Promise.all([
				post("/api/files/", formData).catch(() => null),
				_blobImageSize(blob),
			]).then(([fileResp, imageSize]) => {
				if (fileResp) {
					try { store.dispatch(addCaseFile(fileResp)); } catch (e) { /* ignore */ }
				}
				// With scale:1, bitmap ≈ OL CSS size. Prefer exact CSS size when within ~8%
				// so sites_meta.image_size matches calculateExtent's getSize().
				let publishedSize = imageSize;
				if (
					cssSize && imageSize
					&& cssSize[0] > 0 && cssSize[1] > 0
					&& imageSize[0] > 0 && imageSize[1] > 0
				) {
					const rx = imageSize[0] / cssSize[0];
					const ry = imageSize[1] / cssSize[1];
					if (Math.abs(rx - 1) < 0.08 && Math.abs(ry - 1) < 0.08) {
						publishedSize = [Math.round(cssSize[0]), Math.round(cssSize[1])];
					}
				}
				finish(
					fileResp && fileResp.id,
					fileResp && (fileResp.url_safe || fileResp.file),
					publishedSize || imageSize || (cssSize ? [Math.round(cssSize[0]), Math.round(cssSize[1])] : null),
					blob,
				);
			});
		},
		undefined,
		undefined,
		// scale:1 → bitmap pixels == OL CSS size (fixes DPR shift on save).
		{includeWatermark: false, includeScaleLine: false, scale: 1},
	);
});

export const MAX_AI_SITES = 5;

/** 0 → A, 25 → Z, 26 → AA (serial labels across a chat). */
export const siteLabelAt = (index) => {
	let n = Math.max(0, index) + 1;
	let s = "";
	while (n > 0) {
		n -= 1;
		s = String.fromCharCode(65 + (n % 26)) + s;
		n = Math.floor(n / 26);
	}
	return s;
};

/**
 * POST chat message. Pass `sites` (multi) or legacy single blob/fileId.
 * sites: [{blob, fileId, mapState, label}, ...]
 */
export const sendChatMessageWithOptionalScreenshot = ({
	slide,
	question,
	provider,
	modelId,
	mapState,
	displaySetting,
	captureScreenshot,
	extension = ScreenshotExtensions.JPEG,
	blob: preBlob = undefined,
	fileId: preFileId = undefined,
	sites = undefined,
	annoRequired = false,
	fullAnalysis = true,
	chatId = null,
}) => new Promise((resolve, reject) => {
	const doPostMulti = (siteList) => {
		const formData = new FormData();
		formData.append("slide_id", slide.id);
		formData.append("question", question);
		formData.append("anno_required", annoRequired ? "1" : "0");
		formData.append("full_analysis", fullAnalysis ? "1" : "0");
		if (provider) formData.append("provider", provider);
		if (modelId) formData.append("model_id", modelId);
		if (chatId) formData.append("chat_id", chatId);
		const meta = (siteList || []).map((s, i) => {
			const sc = s.scaleContext || s.scale_context || null;
			return {
				label: s.label || siteLabelAt(i),
				map_state: s.mapState || s.map_state || null,
				extent: s.extent || null,
				image_size: s.imageSize || s.image_size || null,
				viewport_css_size: s.viewportCssSize || s.viewport_css_size || null,
				canvas_size: s.canvasSize || s.canvas_size
					|| s.viewportCssSize || s.viewport_css_size
					|| s.imageSize || s.image_size || null,
				slide_bounds: s.slideBounds || s.slide_bounds || null,
				visible_canvas_box: s.visibleCanvasBox || s.visible_canvas_box || null,
				file_id: s.fileId || s.file_id || null,
				scale_bar: sc?.scale_bar || s.scale_bar || null,
				mpp_um_per_px: sc?.mpp_um_per_px ?? s.mpp_um_per_px ?? null,
				slide_mpp_um_per_px: sc?.slide_mpp_um_per_px ?? s.slide_mpp_um_per_px ?? null,
				finest_mpp_um_per_px: sc?.finest_mpp_um_per_px ?? s.finest_mpp_um_per_px ?? null,
				current_z: sc?.current_z ?? s.current_z ?? null,
				available_mpp_um_per_px: sc?.available_mpp_um_per_px || s.available_mpp_um_per_px || null,
				available_zooms: sc?.available_zooms || s.available_zooms || null,
				// Auto-captured FOVs: which earlier image the request came from (for recall/coverage).
				source_site: s.viewResolved?.sourceSite || s.sourceSite || null,
				view_intent: s.viewIntent || s.viewRequest?.intent || null,
			};
		});
		formData.append("sites_meta", JSON.stringify(meta));
		(siteList || []).forEach((s, i) => {
			if (s.blob) {
				const label = s.label || siteLabelAt(i);
				formData.append(`image_${i}`, s.blob, `ai_viewport_${label}${extension}`);
			}
		});
		if (siteList?.length === 1 && siteList[0].mapState) {
			formData.append("map_state", JSON.stringify(siteList[0].mapState));
		} else if (mapState) {
			formData.append("map_state", JSON.stringify(mapState));
		}
		post_response("/api/slide_ai/chat/message/", formData)
			.then((res) => resolve(res.data))
			.catch(reject);
	};

	const doPost = (blob, fileId) => {
		if (blob) {
			doPostMulti([{blob, fileId, mapState, label: "A"}]);
			return;
		}
		const formData = new FormData();
		formData.append("slide_id", slide.id);
		formData.append("question", question);
		formData.append("anno_required", annoRequired ? "1" : "0");
		formData.append("full_analysis", fullAnalysis ? "1" : "0");
		if (provider) formData.append("provider", provider);
		if (modelId) formData.append("model_id", modelId);
		if (chatId) formData.append("chat_id", chatId);
		if (mapState) formData.append("map_state", JSON.stringify(mapState));
		post_response("/api/slide_ai/chat/message/", formData)
			.then((res) => resolve(res.data))
			.catch(reject);
	};

	if (Array.isArray(sites) && sites.length) {
		doPostMulti(sites.slice(0, MAX_AI_SITES));
		return;
	}

	if (preBlob !== undefined) {
		doPost(preBlob || null, preFileId || null);
		return;
	}

	if (!captureScreenshot) {
		doPost(null, null);
		return;
	}

	captureAiViewportScreenshot({slide, mapState, displaySetting, extension})
		.then(({blob, fileId}) => doPost(blob, fileId))
		.catch(() => doPost(null, null));
});

const _GEOMETRY_TYPES = new Set([
	"Point", "MultiPoint", "LineString", "MultiLineString", "Polygon", "MultiPolygon", "GeometryCollection",
]);

/**
 * FeatureCollection | Feature from what models emit: a bare feature list, {features:[…]}
 * without type, or a bare geometry. Null when nothing usable.
 */
const _coerceGeoJsonRoot = (parsed) => {
	if (!parsed || typeof parsed !== "object") return null;
	const asFeature = (f) => {
		if (!f || typeof f !== "object") return null;
		if (f.type === "Feature" && f.geometry) return f;
		if (_GEOMETRY_TYPES.has(f.type)) return {type: "Feature", geometry: f, properties: {}};
		if (f.geometry && _GEOMETRY_TYPES.has(f.geometry.type)) return {...f, type: "Feature"};
		return null;
	};
	if (Array.isArray(parsed)) {
		const features = parsed.map(asFeature).filter(Boolean);
		return features.length ? {type: "FeatureCollection", features} : null;
	}
	if (parsed.type === "FeatureCollection" || (Array.isArray(parsed.features) && !parsed.type)) {
		return {...parsed, type: "FeatureCollection", features: (parsed.features || []).map(asFeature).filter(Boolean)};
	}
	return asFeature(parsed);
};

/** Split Multi* / GeometryCollection features into one feature per part. */
export const explodeMultiGeometryFeature = (feature) => {
	const geom = feature?.geometry;
	if (!geom) return [feature];
	const single = {MultiPoint: "Point", MultiLineString: "LineString", MultiPolygon: "Polygon"}[geom.type];
	let parts = null;
	if (single) {
		parts = (geom.coordinates || []).map((coordinates) => ({type: single, coordinates}));
	} else if (geom.type === "GeometryCollection") {
		parts = (geom.geometries || []).flatMap((g) => explodeMultiGeometryFeature({geometry: g}).map((f) => f.geometry));
	}
	if (!parts) return [feature];
	const props = feature.properties || {};
	const base = String(props.label || props.name || "").trim();
	if (parts.length === 1) return [{...feature, geometry: parts[0]}];
	return parts.map((g, i) => ({
		...feature,
		geometry: g,
		properties: {...props, ...(base ? {label: `${base}_${i + 1}`} : {})},
	}));
};

/** Parse JSON body that may be a FeatureCollection / Feature / wrapped. */
const _parseAiGeoJsonPayload = (raw, attrMeta = {}) => {
	const body = (raw || "").trim();
	if (!body) return null;
	try {
		const parsed = _coerceGeoJsonRoot(_parseLooseJson(body));
		if (!parsed) return null;
		if (parsed.type === "FeatureCollection") {
			if (!parsed.coordinate_space && !parsed.coordinateSpace && attrMeta.coordinate_space) {
				parsed.coordinate_space = attrMeta.coordinate_space;
			}
			if (!parsed.canvas_size && !parsed.canvasSize && attrMeta.canvas_size) {
				parsed.canvas_size = attrMeta.canvas_size;
			}
			if (!parsed.source_site && attrMeta.source_site) {
				parsed.source_site = attrMeta.source_site;
			}
			// Promote collection source_site onto features missing it.
			const site = parsed.source_site || attrMeta.source_site;
			if (site && Array.isArray(parsed.features)) {
				parsed.features = parsed.features.map((f) => {
					if (!f || typeof f !== "object") return f;
					const props = f.properties || {};
					if (props.source_site || props.site) return f;
					return {
						...f,
						properties: {...props, source_site: site},
					};
				});
			}
		}
		return parsed;
	} catch (e) {
		return null;
	}
};

const _attrsFromAiGeoDivOpenTag = (openTag) => {
	const meta = {coordinate_space: "canvas_pixels"};
	const w = /data-canvas-width=["']?(\d+)/i.exec(openTag);
	const h = /data-canvas-height=["']?(\d+)/i.exec(openTag);
	if (w && h) meta.canvas_size = [Number(w[1]), Number(h[1])];
	const site = /data-source-site=["']([A-Za-z]+)["']/i.exec(openTag)
		|| /data-label=["'][^"']*?\[Image\s+([A-Za-z]+)\]/i.exec(openTag)
		|| /#site:([A-Za-z]+)/i.exec(openTag);
	if (site) meta.source_site = String(site[1]).toUpperCase();
	return meta;
};

/**
 * Extract FeatureCollections from:
 *  - ```ai-geojson ... ```
 *  - <div class="ai-geojson" …>{…}</div>  (model slip; still supported)
 */
export const extractAiGeoJsonBlocks = (content) => {
	const text = content || "";
	const out = [];
	const seen = new Set();
	const push = (parsed) => {
		if (!parsed) return;
		const key = JSON.stringify(parsed);
		if (seen.has(key)) return;
		seen.add(key);
		out.push(parsed);
	};

	const fenceRe = /```ai-geojson\s*([\s\S]*?)```/gi;
	let m;
	while ((m = fenceRe.exec(text)) !== null) {
		push(_parseAiGeoJsonPayload(m[1]));
	}

	const divRe = /<div\b[^>]*\bclass\s*=\s*["'][^"']*\bai-geojson\b[^"']*["'][^>]*>([\s\S]*?)<\/div>/gi;
	while ((m = divRe.exec(text)) !== null) {
		const openTag = m[0].slice(0, m[0].indexOf(">") + 1);
		push(_parseAiGeoJsonPayload(m[1], _attrsFromAiGeoDivOpenTag(openTag)));
	}

	return out;
};

/**
 * Dermatology feature class → color + reading priority (textbook/PPT chips).
 * Keys are matched against properties.class or label (prefix / exact).
 */
export const DERM_FEATURE_CLASSES = {
	epidermis: {color: "#4CAF50", priority: "primary", title: "Epidermis"},
	dermis: {color: "#FF9800", priority: "primary", title: "Dermis"},
	papillary_dermis: {color: "#FFB74D", priority: "primary", title: "Papillary dermis"},
	reticular_dermis: {color: "#F57C00", priority: "primary", title: "Reticular dermis"},
	follicle: {color: "#9C27B0", priority: "primary", title: "Follicle"},
	hair_follicle: {color: "#9C27B0", priority: "primary", title: "Hair follicle"},
	sej: {color: "#00BCD4", priority: "primary", title: "SEJ"},
	dej: {color: "#00ACC1", priority: "primary", title: "DEJ"},
	adnexal: {color: "#7E57C2", priority: "primary", title: "Adnexa"},
	sebaceous: {color: "#AB47BC", priority: "secondary", title: "Sebaceous"},
	vessel: {color: "#EF5350", priority: "secondary", title: "Vessel"},
	inflammation: {color: "#EC407A", priority: "secondary", title: "Inflammation"},
	ulcer: {color: "#C62828", priority: "secondary", title: "Ulcer"},
	surgical_ink: {color: "#424242", priority: "secondary", title: "Surgical ink"},
	ink: {color: "#424242", priority: "secondary", title: "Ink"},
	tissue: {color: "#FFEB3B", priority: "primary", title: "Tissue fragment"},
	tumour: {color: "#D32F2F", priority: "primary", title: "Tumour"},
	measurement: {color: "#FFFFFF", priority: "secondary", title: "Measurement"},
	closer_look: {color: "#E91E63", priority: "secondary", title: "Closer look"},
	roi: {color: "#E91E63", priority: "secondary", title: "ROI"},
	other: {color: "#78909C", priority: "secondary", title: "Other"},
};

const _DEFAULT_FEAT_COLOR = "#E91E63";

/** Resolve class key from properties.class or label (e.g. follicle_2 → follicle). */
export const resolveDermFeatureClass = (labelOrClass) => {
	const raw = String(labelOrClass || "").trim().toLowerCase().replace(/\s+/g, "_");
	if (!raw) return "other";
	if (DERM_FEATURE_CLASSES[raw]) return raw;
	// follicle_1, closer_look_2, hair_follicle_3
	const prefix = raw.replace(/_\d+$/, "");
	if (DERM_FEATURE_CLASSES[prefix]) return prefix;
	if (raw.startsWith("follicle") || raw.startsWith("hair_follicle")) return "follicle";
	if (raw.startsWith("closer_look") || raw.startsWith("roi")) return "closer_look";
	if (raw.startsWith("tissue") || raw.startsWith("fragment") || raw.startsWith("specimen")) return "tissue";
	if (raw.startsWith("tumo") || raw.includes("neoplas") || raw.includes("carcinoma")) return "tumour";
	if (raw.startsWith("measure")) return "measurement";
	if (raw.includes("epiderm")) return "epidermis";
	if (raw.includes("dermis") && raw.includes("papillar")) return "papillary_dermis";
	if (raw.includes("dermis") && raw.includes("reticular")) return "reticular_dermis";
	if (raw.includes("dermis")) return "dermis";
	if (raw.includes("ink")) return "surgical_ink";
	if (raw.includes("vessel") || raw.includes("vascular")) return "vessel";
	if (raw.includes("inflam")) return "inflammation";
	if (raw.includes("sebaceous")) return "sebaceous";
	if (raw.includes("adnex") || raw.includes("eccrine") || raw.includes("sweat")) return "adnexal";
	if (raw === "dej" || raw.includes("dermoepidermal")) return "dej";
	if (raw === "sej" || raw.includes("squamous")) return "sej";
	return "other";
};

export const dermFeatureStyle = (labelOrClass, explicitColor = null) => {
	const cls = resolveDermFeatureClass(labelOrClass);
	const meta = DERM_FEATURE_CLASSES[cls] || DERM_FEATURE_CLASSES.other;
	let color = explicitColor;
	if (color && /^#?[0-9a-fA-F]{6}$/.test(String(color).replace("#", ""))) {
		color = String(color).startsWith("#") ? String(color) : `#${color}`;
	} else {
		color = meta.color || _DEFAULT_FEAT_COLOR;
	}
	return {
		classKey: cls,
		color,
		priority: meta.priority || "secondary",
		title: meta.title || cls,
	};
};

/** Flatten AI geo features for per-feature Draw UI + bulk save. */
export const listAiGeoFeatures = (content) => {
	const blocks = extractAiGeoJsonBlocks(content);
	const out = [];
	let index = 0;
	blocks.forEach((b) => {
		const pushFeat = (f, collectionMeta) => {
			const props = f?.properties || {};
			const label = (props.label || props.name || `Region ${index + 1}`).toString().slice(0, 64);
			const sourceSite = String(props.source_site || props.site || "").toUpperCase() || null;
			const classHint = props.class || props.feature_class || label;
			const style = dermFeatureStyle(classHint, props.color);
			const priority = String(props.priority || style.priority || "secondary").toLowerCase() === "primary"
				? "primary"
				: "secondary";
			const canvas = collectionMeta?.canvas_size || collectionMeta?.canvasSize || null;
			const suspectFovFill = isAiFeatureSuspectFovFill(f, canvas);
			out.push({
				index,
				key: `${index}:${sourceSite || "_"}:${label}`,
				feature: f,
				collectionMeta: collectionMeta || {},
				label,
				sourceSite,
				color: style.color,
				classKey: style.classKey,
				priority,
				displayTitle: style.title,
				tool: props.tool || props.tool_type || null,
				suspectFovFill,
			});
			index += 1;
		};
		if (b.type === "FeatureCollection") {
			const meta = {
				coordinate_space: b.coordinate_space || b.coordinateSpace,
				extent: b.extent,
				image_size: b.image_size || b.imageSize,
				canvas_size: b.canvas_size || b.canvasSize,
				source_site: b.source_site || b.sourceSite || null,
			};
			(b.features || []).flatMap(explodeMultiGeometryFeature).forEach((f) => {
				const props = f?.properties || {};
				if (!props.source_site && !props.site && meta.source_site) {
					pushFeat({
						...f,
						properties: {...props, source_site: meta.source_site},
					}, meta);
				} else {
					pushFeat(f, meta);
				}
			});
		} else if (b.type === "Feature") {
			explodeMultiGeometryFeature(b).forEach((f) => pushFeat(f, {}));
		}
	});
	return out;
};

/** Group features for legend: primary first, then secondary. */
export const groupAiGeoFeaturesByPriority = (items) => {
	const primary = [];
	const secondary = [];
	(items || []).forEach((it) => {
		if (it.priority === "primary") primary.push(it);
		else secondary.push(it);
	});
	return {primary, secondary};
};

/**
 * Turn bare feature labels in prose into markdown links `#feat:INDEX`
 * so the reader can Go to / Draw inline (textbook-style).
 * Only links labels that appear in geoItems; longest labels first.
 */
export const linkifyFeatureRefs = (content, geoItems = []) => {
	let text = content || "";
	if (!geoItems.length) return text;
	const known = new Set(geoItems.map((it) => String(it.label || "").toLowerCase()).filter(Boolean));
	// Models often write labels as `code` (or `[label](#feat:N)`): unwrap so they become chips.
	text = text.replace(/`\[?([\w-]+)\]?(?:\(#feat:\d+\))?`/g, (m, lab) => (
		known.has(lab.toLowerCase()) ? lab : m
	));
	// Protect existing markdown links
	const holes = [];
	text = text.replace(/\[[^\]]*\]\([^)]+\)/g, (m) => {
		holes.push(m);
		return `\u0000L${holes.length - 1}\u0000`;
	});
	const byLabel = new Map();
	geoItems.forEach((it) => {
		const k = String(it.label || "").toLowerCase();
		if (k && !byLabel.has(k)) byLabel.set(k, it);
	});
	const labels = Array.from(byLabel.keys()).sort((a, b) => b.length - a.length);
	labels.forEach((lab) => {
		const it = byLabel.get(lab);
		if (!it) return;
		// word-ish boundary; allow follicle_1
		const esc = lab.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		const re = new RegExp(`(?<![\\w/#])(${esc})(?![\\w]|\\]\\()`, "gi");
		text = text.replace(re, (match) => `[${match}](#feat:${it.index})`);
	});
	text = text.replace(/\u0000L(\d+)\u0000/g, (_, i) => holes[Number(i)] || "");
	return text;
};

/** Detected regions (R1…, B1…) of one attachment, flattened: [{id, bbox, centroid, contour, kind, inRegion}]. */
export const attachmentRegions = (att) => {
	const det = att?.detected;
	if (!det) return [];
	const out = [];
	(det.tissue_regions || []).forEach((r) => r?.id && out.push({
		id: String(r.id).toUpperCase(), kind: "tissue", bbox: r.bbox || null, centroid: r.centroid || null,
		contour: r.contour || null, areaPct: r.area_pct_of_image ?? null,
	}));
	(det.basophilic_regions || []).forEach((r) => r?.id && out.push({
		id: String(r.id).toUpperCase(), kind: "basophilic", bbox: r.bbox || null, centroid: r.centroid || null,
		contour: null, inRegion: r.in_region || null, areaPct: r.area_pct_of_image ?? null,
	}));
	return out;
};

/**
 * Walk prose outside markdown links, tracking the current image letter from `Image X` /
 * `#site:X` mentions, and let `replaceSegment(segment, currentLabel)` rewrite each segment.
 */
const _rewriteOutsideLinks = (text, replaceSegment, defaultLabel = null) => {
	// Skip existing markdown links and inline code spans.
	const parts = (text || "").split(/(\[[^\]]*\]\([^)]+\)|`[^`\n]*`)/);
	let current = defaultLabel;
	return parts.map((p, i) => {
		if (i % 2 === 1) {
			const m = /#site:([A-Za-z]{1,3})\)/.exec(p);
			if (m) current = m[1].toUpperCase();
			return p;
		}
		// Plain "Image X" (before linkifySiteRefs) also sets context; rewrite piecewise.
		const chunks = p.split(/(\bImage\s+[A-Z]{1,3}\b)/);
		return chunks.map((c, j) => {
			if (j % 2 === 1) {
				current = c.replace(/^Image\s+/, "").toUpperCase();
				return c;
			}
			return replaceSegment(c, current);
		}).join("");
	}).join("");
};

/**
 * Rewrite detected-region ids (R1, B2) in prose into `#region:IMAGE:ID` links so the
 * reader can jump to / outline them. Only ids present on a known image are linked.
 */
export const linkifyRegionRefs = (content, attachments = [], defaultLabel = null) => {
	// Models often wrap references in backticks (`R1`, `Image B [300, 450]`): unwrap so they
	// become chips instead of raw link syntax inside inline code.
	content = (content || "")
		.replace(/`((?:Image\s+[A-Z]{1,3}\s*)?~?\[[\d\s.,–—-]+\])`/g, "$1")
		.replace(/`(Image\s+[A-Z]{1,3})`/g, "$1")
		.replace(/`([RB]\d{1,2}(?:\s*[–-]\s*[RB]\d{1,2})?)`/g, "$1");
	const byImage = new Map();
	(attachments || []).forEach((a) => {
		const L = String(a?.label || "").toUpperCase();
		const ids = new Set(attachmentRegions(a).map((r) => r.id));
		if (L && ids.size) byImage.set(L, ids);
	});
	if (!byImage.size) return content || "";
	const ownerOf = (id, current) => {
		if (current && byImage.get(current)?.has(id)) return current;
		const owners = [...byImage.entries()].filter(([, ids]) => ids.has(id)).map(([L]) => L);
		return owners.length === 1 ? owners[0] : (owners.includes(current) ? current : null);
	};
	return _rewriteOutsideLinks(content, (seg, current) => seg.replace(
		/(^|[^\w#:/])(\*{0,2})\b([RB]\d{1,2})\b(\*{0,2})(?![\w\]\)])/g,
		(m, pre, b1, id, b2) => {
			const L = ownerOf(id.toUpperCase(), current);
			return L ? `${pre}${b1}[${id}](#region:${L}:${id.toUpperCase()})${b2}` : m;
		},
	), defaultLabel);
};

/**
 * Rewrite bare canvas coordinates — `[x,y]`, `~[x, y]`, `[x0–x1, y0–y1]` — into
 * `#coord:IMAGE:x:y` / `#box:IMAGE:x0:y0:x1:y1` links. Image = nearest preceding mention,
 * else `defaultLabel`; coordinates outside that image's canvas are left alone.
 */
export const linkifyCoordRefs = (content, attachments = [], defaultLabel = null) => {
	const canvasOf = new Map();
	(attachments || []).forEach((a) => {
		const L = String(a?.label || "").toUpperCase();
		const size = a?.canvas_size || a?.viewport_css_size || a?.image_size;
		if (L && Array.isArray(size) && size.length >= 2) canvasOf.set(L, size);
	});
	if (!canvasOf.size) return content || "";
	const fallback = defaultLabel || (canvasOf.size === 1 ? [...canvasOf.keys()][0] : null);
	const inside = (L, x, y) => {
		const s = canvasOf.get(L);
		return s && x >= 0 && y >= 0 && x <= s[0] * 1.02 && y <= s[1] * 1.02;
	};
	const num = "\\d{1,4}(?:\\.\\d+)?";
	const range = `(${num})\\s*[–—-]\\s*(${num})`;
	const boxRe = new RegExp(`~?\\[\\s*${range}\\s*,\\s*${range}\\s*\\]`, "g");
	const ptRe = new RegExp(`~?\\[\\s*(${num})\\s*,\\s*(${num})\\s*\\]`, "g");
	return _rewriteOutsideLinks(content, (seg, current) => {
		const L = current || fallback;
		if (!L) return seg;
		let s = seg.replace(boxRe, (m, x0, x1, y0, y1) => {
			const v = [x0, x1, y0, y1].map(Number);
			if (!inside(L, v[1], v[3]) || v[0] > v[1] || v[2] > v[3]) return m;
			return `[\\[${x0}–${x1}, ${y0}–${y1}\\]](#box:${L}:${v[0]}:${v[2]}:${v[1]}:${v[3]})`;
		});
		s = s.replace(ptRe, (m, x, y) => {
			const px = Number(x);
			const py = Number(y);
			if (!inside(L, px, py)) return m;
			return `[\\[${x}, ${y}\\]](#coord:${L}:${px}:${py})`;
		});
		return s;
	}, defaultLabel);
};

/** Synthetic geo item (same shape as listAiGeoFeatures output) for a region / point / box. */
export const syntheticGeoItem = ({label, sourceSite, kind, point = null, bbox = null, contour = null, color = "#9ecbff"}) => {
	let geometry;
	if (contour && contour.length >= 3) {
		geometry = {type: "Polygon", coordinates: [[...contour, contour[0]]]};
	} else if (bbox && bbox.length >= 4) {
		const [x0, y0, x1, y1] = bbox.map(Number);
		geometry = {type: "Polygon", coordinates: [[[x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, y0]]]};
	} else if (point) {
		geometry = {type: "Point", coordinates: [Number(point[0]), Number(point[1])]};
	} else {
		return null;
	}
	const feature = {
		type: "Feature",
		geometry,
		properties: {label, source_site: sourceSite, class: kind, color, provenance: "viewer"},
	};
	return {
		index: -1,
		key: `synthetic:${sourceSite}:${label}`,
		feature,
		collectionMeta: {coordinate_space: "canvas_pixels", source_site: sourceSite},
		label,
		sourceSite,
		color,
		classKey: kind,
		priority: "secondary",
		displayTitle: label,
		tool: geometry.type === "Point" ? "Arrow" : (bbox && !contour ? "Rectangle" : "Polygon"),
		suspectFovFill: false,
	};
};

/** Strip machine ai-geojson fences / div wrappers from display text. */
export const stripAiGeoJsonBlocks = (content) => {
	if (!content) return "";
	return content
		.replace(/```ai-geojson\s*[\s\S]*?```/gi, "")
		.replace(/<div\b[^>]*\bclass\s*=\s*["'][^"']*\bai-geojson\b[^"']*["'][^>]*>[\s\S]*?<\/div>/gi, "")
		.trim();
};

const _VIEW_REQUEST_FENCE_RE = /```(?:ai-view-requests?|view-requests?)[ \t]*\n([\s\S]*?)```/gi;
const _VIEW_REQUEST_STRIP_RE = /```(?:ai-view-requests?|view-requests?)[ \t]*\n[\s\S]*?```/gi;

/** JSON.parse that tolerates // comments and trailing commas (common model slips). */
const _parseLooseJson = (body) => {
	const raw = (body || "").trim();
	if (!raw) return null;
	try {
		return JSON.parse(raw);
	} catch (e) { /* retry below */ }
	try {
		const cleaned = raw
			.replace(/^\s*\/\/.*$/gm, "")
			.replace(/,\s*([}\]])/g, "$1");
		return JSON.parse(cleaned);
	} catch (e) {
		return null;
	}
};

/** Extract ```ai-view-request ... ``` blocks (any JSON object or array). */
export const extractAiViewRequestBlocks = (content) => {
	const text = content || "";
	const out = [];
	let m;
	_VIEW_REQUEST_FENCE_RE.lastIndex = 0;
	while ((m = _VIEW_REQUEST_FENCE_RE.exec(text)) !== null) {
		const parsed = _parseLooseJson(m[1]);
		if (parsed && typeof parsed === "object") out.push(parsed);
	}
	return out;
};

export const stripAiViewRequestBlocks = (content) => {
	if (!content) return "";
	return content.replace(_VIEW_REQUEST_STRIP_RE, "").trim();
};

const _VR_LIST_KEYS = ["requests", "view_requests", "viewRequests", "views", "sites", "targets", "regions", "fovs", "items"];
const _VR_POINT_KEYS = [
	"point", "center", "center_px", "center_canvas_px", "center_canvas_pixels", "preferred_center_canvas_px",
	"canvas_xy", "canvas_point", "canvas_pixels", "canvas_px", "focus_canvas_pixels", "focus_canvas_px",
	"focus", "focus_px", "xy", "position", "target_point", "crop_anchor",
];
const _VR_BBOX_KEYS = ["bbox", "canvas_bbox", "bbox_canvas_px", "bbox_canvas_pixels", "canvas_rect", "rect", "box", "region"];
const _VR_SITE_KEYS = ["source_site", "site", "from_site", "source_image", "image", "source"];
const _VR_Z_KEYS = ["target_z", "preferred_z", "target_zoom", "z", "zoom"];
const _VR_MAG_KEYS = ["target_mag", "target_magnification", "magnification", "mag", "target_x", "objective"];
/** "10x" / "10×" / 10 → 10 */
const _magNum = (v) => {
	if (v == null) return null;
	const m = /^\s*~?\s*(\d+(?:\.\d+)?)\s*[x×]?\s*$/i.exec(String(v));
	return m ? Number(m[1]) : null;
};

/** Nominal magnification label ("10×") for z on a zoom ladder; null when unknown. */
export const magLabelForZ = (z, zooms) => {
	if (z == null || !Array.isArray(zooms)) return null;
	const hit = zooms.find((e) => e && Number(e.z) === Number(z) && e.optical_x != null);
	return hit ? `${Number(hit.optical_x)}×` : null;
};

/** "2.5×→10×" when both ends are known, else "z3→z5". */
export const zoomChangeText = (fromZ, toZ, zooms) => {
	const a = magLabelForZ(fromZ, zooms);
	const b = magLabelForZ(toZ, zooms);
	if (a && b) return `${a}→${b}`;
	if (fromZ == null || toZ == null) return "";
	return `z${fromZ}→z${toZ}`;
};
const _VR_MPP_KEYS = ["target_mpp_um_per_px", "target_mpp", "mpp_um_per_px", "mpp"];
const _VR_PART_KEYS = ["part", "target_part", "slide_part", "source_part"];
const _VR_INTENTS = {
	higher_res: ["higher_res", "higher", "zoom_in", "zoomin", "closer", "closer_look", "finer", "detail", "zoom"],
	lower_res: ["lower_res", "lower", "zoom_out", "zoomout", "context", "overview", "coarser"],
	nearby: ["nearby", "pan", "pan_adjacent", "adjacent", "neighbor", "neighbour", "shift"],
	recenter: ["recenter", "re-center", "center", "centre"],
};

const _num = (v) => {
	const n = Number(v);
	return v != null && v !== "" && Number.isFinite(n) ? n : null;
};

/** [x, y] from an array or {x, y} object (canvas pixels). */
const _asPair = (v) => {
	if (Array.isArray(v) && v.length >= 2 && !Array.isArray(v[0])) {
		const x = _num(v[0]);
		const y = _num(v[1]);
		return x != null && y != null ? [x, y] : null;
	}
	if (v && typeof v === "object") {
		const x = _num(v.x ?? v.cx);
		const y = _num(v.y ?? v.cy);
		return x != null && y != null ? [x, y] : null;
	}
	return null;
};

/** [x0, y0, x1, y1] (sorted) from an array or {x0,y0,x1,y1} / {minX..} / {x,y,w,h}. */
const _asBox = (v) => {
	let box = null;
	if (Array.isArray(v) && v.length >= 4 && !Array.isArray(v[0])) {
		box = v.slice(0, 4).map(_num);
	} else if (Array.isArray(v) && v.length === 2 && Array.isArray(v[0]) && Array.isArray(v[1])) {
		box = [_num(v[0][0]), _num(v[0][1]), _num(v[1][0]), _num(v[1][1])];
	} else if (v && typeof v === "object") {
		if (v.x0 != null || v.x1 != null) box = [_num(v.x0), _num(v.y0), _num(v.x1), _num(v.y1)];
		else if (v.minX != null || v.min_x != null) {
			box = [_num(v.minX ?? v.min_x), _num(v.minY ?? v.min_y), _num(v.maxX ?? v.max_x), _num(v.maxY ?? v.max_y)];
		} else if (v.x != null && (v.w != null || v.width != null)) {
			const x = _num(v.x);
			const y = _num(v.y);
			const w = _num(v.w ?? v.width);
			const h = _num(v.h ?? v.height);
			box = [x, y, x != null && w != null ? x + w : null, y != null && h != null ? y + h : null];
		}
	}
	if (!box || !box.every((n) => n != null)) return null;
	const [a, b, c, d] = box;
	return [Math.min(a, c), Math.min(b, d), Math.max(a, c), Math.max(b, d)];
};

const _firstOf = (obj, keys, fn) => {
	for (const k of keys) {
		if (obj[k] == null) continue;
		const v = fn ? fn(obj[k]) : obj[k];
		if (v != null) return v;
	}
	return null;
};

const _normalizeIntent = (raw) => {
	const s = String(raw || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
	if (!s) return null;
	for (const [intent, aliases] of Object.entries(_VR_INTENTS)) {
		if (aliases.includes(s)) return intent;
	}
	if (s.includes("high") || s.includes("zoom_in") || s.includes("fine")) return "higher_res";
	if (s.includes("low") || s.includes("zoom_out")) return "lower_res";
	if (s.includes("pan") || s.includes("near") || s.includes("adjac")) return "nearby";
	return null;
};

/** Items in one parsed block; block-level source_site is inherited by items. */
const _viewRequestItems = (block) => {
	if (Array.isArray(block)) return block;
	if (!block || typeof block !== "object") return [];
	const blockSite = _firstOf(block, _VR_SITE_KEYS, (v) => (typeof v === "string" ? v : null));
	for (const k of _VR_LIST_KEYS) {
		if (Array.isArray(block[k]) && block[k].some((r) => r && typeof r === "object")) {
			return block[k].map((r) => (
				r && typeof r === "object" && blockSite && _firstOf(r, _VR_SITE_KEYS) == null
					? {...r, source_site: blockSite}
					: r
			));
		}
	}
	return [block];
};

/**
 * Normalize one raw view-request item (any key spelling the model used) into
 * {intent, sourceSite, point, bbox, offsetCanvas, slideCenter, targetZ, targetMpp, ...}.
 * Returns null when the item carries no usable target at all.
 */
export const normalizeViewRequestItem = (r, index = 0, {geoItems = []} = {}) => {
	if (!r || typeof r !== "object") return null;
	let point = _firstOf(r, _VR_POINT_KEYS, (v) => (_asBox(v) ? null : _asPair(v)));
	let bbox = _firstOf(r, _VR_BBOX_KEYS, _asBox) || _firstOf(r, _VR_POINT_KEYS, _asBox);
	if (!bbox && r.geometry) bbox = (() => {
		const b = featureCanvasBBox({geometry: r.geometry});
		return b ? [b.minX, b.minY, b.maxX, b.maxY] : null;
	})();
	let sourceSite = _firstOf(r, _VR_SITE_KEYS, (v) => {
		if (typeof v !== "string" || !v.trim()) return null;
		const m = /^(?:image[\s_-]*)?([a-z]{1,3})(?:\.\w+)?$/i.exec(v.trim());
		return m ? m[1] : null;
	});
	// Reference to a closer_look feature emitted in the same reply.
	if (!point && !bbox && geoItems.length) {
		const ref = String(r.closer_look_id || r.feature || r.label || r.id || "").toLowerCase();
		const hit = ref && geoItems.find((g) => String(g.label || "").toLowerCase() === ref);
		if (hit) {
			const b = featureCanvasBBox(hit.feature);
			if (b) bbox = [b.minX, b.minY, b.maxX, b.maxY];
			if (!sourceSite && hit.sourceSite) sourceSite = hit.sourceSite;
		}
	}
	const ms = r.map_state || r.mapState || null;
	const slideCenter = ms && _num(ms.x) != null && _num(ms.y) != null ? [_num(ms.x), _num(ms.y)] : null;
	const offsetCanvas = _asPair(r.offset_canvas || r.offset || r.pan_offset);
	const targetZ = _firstOf(r, _VR_Z_KEYS, _num);
	const targetMpp = _firstOf(r, _VR_MPP_KEYS, _num);
	const targetMag = _firstOf(r, _VR_MAG_KEYS, _magNum);
	const hasRegion = Boolean(point || bbox || slideCenter);
	const intent = _normalizeIntent(r.intent || r.request_type || r.action)
		|| (offsetCanvas ? "nearby" : (hasRegion || targetZ != null || targetMpp != null || targetMag != null ? "higher_res" : "recenter"));
	const id = String(r.id || r.closer_look_id || r.label || r.label_hint || r.labelHint || `vr${index + 1}`).slice(0, 64);
	const targetPart = _firstOf(r, _VR_PART_KEYS, (v) => {
		const m = /^(?:part[\s_-]*)?(\d{1,3})$/i.exec(String(v ?? "").trim());
		return m ? `Part ${Number(m[1])}` : null;
	});
	return {
		index,
		key: `vr:${index}:${id}`,
		id,
		intent,
		sourceSite: sourceSite ? sourceSite.toUpperCase() : null,
		targetPart,
		targetSlideId: typeof r.slide_id === "string" && r.slide_id.length >= 8 ? r.slide_id : null,
		point: point || (bbox ? [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2] : null),
		bbox,
		offsetCanvas,
		slideCenter,
		targetZ,
		targetMpp,
		targetMag,
		reason: String(r.reason || r.label_hint || r.labelHint || "").slice(0, 200),
		raw: r,
	};
};

/** Strip all machine fences from reader prose. */
export const stripAiMachineBlocks = (content) =>
	stripAiViewRequestBlocks(stripAiGeoJsonBlocks(content || ""));

/** Flatten view requests for UI + auto-capture. */
export const listAiViewRequests = (content) => {
	const blocks = extractAiViewRequestBlocks(content);
	if (!blocks.length) return [];
	const geoItems = listAiGeoFeatures(content);
	const out = [];
	const ids = new Set();
	blocks.forEach((b) => {
		_viewRequestItems(b).forEach((r) => {
			const req = normalizeViewRequestItem(r, out.length, {geoItems});
			if (!req) return;
			if (ids.has(req.id)) req.id = `${req.id}_${out.length + 1}`;
			ids.add(req.id);
			req.key = `vr:${out.length}:${req.id}`;
			out.push(req);
		});
	});
	return out.slice(0, MAX_VIEW_REQUESTS_PER_ROUND * 2); // parse cap; fulfill uses tighter cap
};

/** Count ```ai-view-request``` fences whose body is not parseable JSON (for user notice). */
export const countUnparsedViewRequestBlocks = (content) => {
	const text = content || "";
	let bad = 0;
	let m;
	_VIEW_REQUEST_FENCE_RE.lastIndex = 0;
	while ((m = _VIEW_REQUEST_FENCE_RE.exec(text)) !== null) {
		const parsed = _parseLooseJson(m[1]);
		if (!parsed || !_viewRequestItems(parsed).some((r) => r && typeof r === "object")) bad += 1;
	}
	return bad;
};

/**
 * Resolve a view request → map_state {x,y,z,r} using CaptureFrame (rotation-aware).
 */
export const resolveViewRequestMapState = (req, attachments = [], {
	currentMapState = null,
	slidemap = null,
	slide = null,
} = {}) => {
	const list = attachments || [];
	const site = (req.sourceSite && list.find((a) => String(a.label || "").toUpperCase() === req.sourceSite))
		|| list[0]
		|| null;
	const baseMs = site?.map_state || site?.mapState || currentMapState || {};
	const canvas = site?.canvas_size || site?.canvasSize
		|| site?.viewport_css_size || site?.viewportCssSize
		|| site?.image_size || site?.imageSize;
	const frame = buildCaptureFrame({
		mapState: baseMs,
		canvasSize: canvas,
		viewportCssSize: site?.viewport_css_size || site?.viewportCssSize,
		imageSize: site?.image_size || site?.imageSize,
		extent: site?.extent || null,
		slidemap,
		slide,
	});
	let x = frame ? frame.center[0] : Number(baseMs.x);
	let y = frame ? frame.center[1] : Number(baseMs.y);

	const point = req.point || (req.bbox && req.bbox.length >= 4
		? [(Number(req.bbox[0]) + Number(req.bbox[2])) / 2, (Number(req.bbox[1]) + Number(req.bbox[3])) / 2]
		: null);
	if (point) {
		let slidePt = frame ? canvasPixelToSlide(frame, point[0], point[1]) : null;
		if (!slidePt && site?.extent && canvas) {
			slidePt = imagePixelToSlide(point[0], point[1], {extent: site.extent, imageSize: canvas});
		}
		if (slidePt) {
			x = slidePt[0];
			y = slidePt[1];
		}
	} else if (req.slideCenter) {
		[x, y] = req.slideCenter;
	}
	if (req.offsetCanvas && frame) {
		const ox = Number(req.offsetCanvas[0]) || 0;
		const oy = Number(req.offsetCanvas[1]) || 0;
		const shifted = canvasPixelToSlide(
			frame,
			(point ? Number(point[0]) : frame.size[0] / 2) + ox,
			(point ? Number(point[1]) : frame.size[1] / 2) + oy,
		);
		if (shifted) {
			x = shifted[0];
			y = shifted[1];
		}
	}

	// Only zooms the viewer can reach right now (not the slide's full tile ladder).
	let ladder = readReachableZooms({slide, slidemap});
	if (!ladder.length) {
		ladder = (site?.available_zooms || site?.availableZooms || [])
			.map((e, i) => ({z: e?.z != null ? Number(e.z) : i, mpp_um_per_px: Number(e?.mpp_um_per_px ?? e)}))
			.filter((e) => Number.isFinite(e.z));
	}
	const zMin = ladder.length ? ladder[0].z : 0;
	const zMax = ladder.length ? ladder[ladder.length - 1].z : 20;
	const clampZ = (n) => Math.max(zMin, Math.min(zMax, Math.round(n)));

	// Source zoom from the frame's true resolution (stored z can be off for old captures).
	let baseZ = baseMs.z != null && Number.isFinite(Number(baseMs.z)) ? Number(baseMs.z) : null;
	try {
		const view = slidemap?.getView?.();
		if (frame?.resolution && view?.getZoomForResolution) {
			const zr = view.getZoomForResolution(frame.resolution);
			if (Number.isFinite(zr)) baseZ = zr;
		}
	} catch (e) { /* keep stored z */ }
	baseZ = baseZ != null ? clampZ(baseZ) : null;

	const zForMpp = (mpp) => {
		let best = null;
		let bestDiff = Infinity;
		ladder.forEach((e) => {
			if (!(e.mpp_um_per_px > 0)) return;
			const d = Math.abs(Math.log(e.mpp_um_per_px / mpp));
			if (d < bestDiff) {
				bestDiff = d;
				best = e.z;
			}
		});
		return best;
	};

	const zForMag = (mag) => {
		let best = null;
		let bestDiff = Infinity;
		ladder.forEach((e) => {
			if (!(e.optical_x > 0)) return;
			const d = Math.abs(Math.log(e.optical_x / mag));
			if (d < bestDiff) {
				bestDiff = d;
				best = e.z;
			}
		});
		return best;
	};

	let z = baseZ;
	if (req.targetZ != null && Number.isFinite(req.targetZ)) {
		z = clampZ(req.targetZ);
	} else if (req.targetMag != null && req.targetMag > 0 && ladder.some((e) => e.optical_x > 0)) {
		z = zForMag(req.targetMag);
	} else if (req.targetMpp != null && req.targetMpp > 0 && ladder.length) {
		z = zForMpp(req.targetMpp);
	} else if (req.fitBbox || (req.bbox && req.bbox.length >= 4)) {
		const fb = req.fitBbox || {
			minX: Number(req.bbox[0]), minY: Number(req.bbox[1]),
			maxX: Number(req.bbox[2]), maxY: Number(req.bbox[3]),
			width: Math.abs(Number(req.bbox[2]) - Number(req.bbox[0])),
			height: Math.abs(Number(req.bbox[3]) - Number(req.bbox[1])),
		};
		z = fitZoomForCanvasBBox(fb, canvas || frame?.size, baseZ, {
			zMax,
			fillFrac: 0.6,
			minStep: req.intent === "higher_res" ? 1 : 0,
		});
	} else if (req.intent === "higher_res" && baseZ != null) {
		z = baseZ + MAX_ZOOM_STEP_PER_ROUND;
	} else if (req.intent === "lower_res" && baseZ != null) {
		z = baseZ - 1;
	}
	if (z == null) z = Number.isFinite(Number(currentMapState?.z)) ? Number(currentMapState.z) : zMin;
	const requestedZ = Math.round(z);
	if (baseZ != null && z > baseZ + MAX_ZOOM_STEP_PER_ROUND) z = baseZ + MAX_ZOOM_STEP_PER_ROUND;
	z = clampZ(z);

	if (![x, y].every(Number.isFinite)) return null;
	return {
		x,
		y,
		z,
		r: frame ? frame.rotation : (baseMs.r != null ? baseMs.r : (currentMapState?.r ?? 0)),
		labelHint: req.id || req.intent,
		reason: req.reason,
		intent: req.intent,
		sourceSite: site?.label ? String(site.label).toUpperCase() : req.sourceSite,
		sourceZ: baseZ,
		requestedZ,
		zCapped: requestedZ > z,
		zMax,
		zooms: ladder,
	};
};

/**
 * If the model asked for more FOVs in prose / closer_look geojson but omitted
 * ```ai-view-request```, synthesize view requests so Auto-capture still fires.
 */
export const synthesizeViewRequestsFromContent = (content) => {
	const existing = listAiViewRequests(content);
	if (existing.length) return existing;
	const feats = listAiGeoFeatures(content).filter((f) => isCloserLookFeature(f));
	// Only synthesize from explicit closer_look / ROI features (not all primary).
	if (!feats.length) return [];
	return feats.slice(0, MAX_VIEW_REQUESTS_PER_ROUND).map((f, i) => {
		const bbox = featureCanvasBBox(f.feature);
		const point = featureCanvasCentroid(f.feature);
		const props = f.feature?.properties || {};
		return {
			index: i,
			key: `synth:${f.key}`,
			id: f.label || `vr${i + 1}`,
			intent: "higher_res",
			sourceSite: f.sourceSite,
			point,
			bbox: bbox ? [bbox.minX, bbox.minY, bbox.maxX, bbox.maxY] : null,
			fitBbox: bbox,
			offsetCanvas: null,
			targetZ: props.target_z != null ? Number(props.target_z) : null,
			targetMpp: props.target_mpp_um_per_px != null ? Number(props.target_mpp_um_per_px) : null,
			targetMag: _magNum(props.target_mag ?? props.target_magnification ?? props.magnification),
			reason: `Closer look at ${f.label}`,
			raw: {synthesized: true, from: "closer_look"},
		};
	});
};

/** In-flight tiles: the map's TileQueue (OL 7 sources have no per-source loading count). */
export const countMapTilesLoading = (slidemap) => {
	try {
		const q = slidemap?.getTileQueue?.() || slidemap?.tileQueue_;
		if (q && typeof q.getTilesLoading === "function") {
			return (Number(q.getTilesLoading()) || 0) + (Number(q.getCount?.()) || 0);
		}
	} catch (e) { /* ignore */ }
	return 0;
};

/**
 * Wait for the map's `rendercomplete` (all visible tiles loaded, no animation or
 * interaction), then a short quiet period with nothing in flight.
 * Returns true when settled, false on timeout.
 */
export const waitForMapIdle = (slidemap, {
	timeoutMs = 12000,
	quietMs = 250,
} = {}) => new Promise((resolve) => {
	if (!slidemap?.once) {
		resolve(false);
		return;
	}
	let finished = false;
	let quietTimer = null;
	let key = null;
	const finish = (ok) => {
		if (finished) return;
		finished = true;
		clearTimeout(hardTimeout);
		if (quietTimer) clearTimeout(quietTimer);
		if (key) {
			try { slidemap.un("rendercomplete", onComplete); } catch (e) { /* ignore */ }
		}
		resolve(ok);
	};
	const onComplete = () => {
		if (finished) return;
		if (quietTimer) clearTimeout(quietTimer);
		quietTimer = setTimeout(() => {
			if (countMapTilesLoading(slidemap) === 0) finish(true);
			else slidemap.render?.();
		}, quietMs);
	};
	const hardTimeout = setTimeout(() => finish(countMapTilesLoading(slidemap) === 0), timeoutMs);
	try {
		key = slidemap.on("rendercomplete", onComplete);
		slidemap.render?.();
	} catch (e) {
		finish(false);
	}
});

/**
 * Move the view to mapState and confirm it got there: animate, then compare the live
 * center/resolution with the target and set it directly if the animation was cut short
 * or constrained. Waits for tiles. Returns {arrived, settled, mapState: live state}.
 */
export const navigateToMapStateVerified = async (slidemap, target, {
	duration = 320,
	retries = 2,
	tolerancePx = 3,
	timeoutMs = 12000,
} = {}) => {
	const view = slidemap?.getView?.();
	if (!view || !target) return {arrived: false, settled: false, mapState: readLiveMapState(slidemap)};
	const zMin = view.getMinZoom?.() ?? 0;
	const zMax = view.getMaxZoom?.() ?? 28;
	const z = Math.max(zMin, Math.min(zMax, Number(target.z)));
	const goal = {
		x: Number(target.x),
		y: Number(target.y),
		z,
		r: target.r != null ? Number(target.r) : view.getRotation(),
	};
	const goalRes = view.getResolutionForZoom(z);
	const isThere = () => {
		const live = readLiveMapState(slidemap);
		if (!live) return false;
		const dz = Math.abs(live.resolution / goalRes - 1);
		const dPx = Math.hypot(live.x - goal.x, live.y - goal.y) / live.resolution;
		return dz < 0.02 && dPx <= tolerancePx && Math.abs((live.r || 0) - goal.r) < 1e-3;
	};
	await animateToMapStateAsync(view, goal, duration);
	let arrived = isThere();
	for (let i = 0; !arrived && i < retries; i += 1) {
		try {
			view.cancelAnimations?.();
			view.setRotation(goal.r);
			view.setZoom(goal.z);
			view.setCenter([goal.x, goal.y]);
			slidemap.renderSync?.();
		} catch (e) { /* ignore */ }
		await _sleep(60);
		arrived = isThere();
	}
	const settled = await waitForMapIdle(slidemap, {timeoutMs});
	if (!settled) {
		await _sleep(400);
		await waitForMapIdle(slidemap, {timeoutMs: 4000});
	}
	return {arrived, settled, mapState: readLiveMapState(slidemap)};
};

/** Clamp map_state center into slide bounds (keep z/r). */
export const clampMapStateToSlideBounds = (mapState, slideBounds) => {
	if (!mapState || !slideBounds || slideBounds.length < 4) return mapState;
	const [minX, minY, maxX, maxY] = slideBounds.map(Number);
	if (![minX, minY, maxX, maxY].every(Number.isFinite)) return mapState;
	const padX = Math.max(0, (maxX - minX) * 0.02);
	const padY = Math.max(0, (maxY - minY) * 0.02);
	const x = Math.min(maxX - padX, Math.max(minX + padX, Number(mapState.x)));
	const y = Math.min(maxY - padY, Math.max(minY + padY, Number(mapState.y)));
	return {...mapState, x, y};
};

/**
 * Rough empty/white FOV detector on a capture blob.
 * Returns {ok, whiteRatio} — ok=false when mostly blank (tiles missing / off tissue).
 */
export const assessCaptureTissue = (blob, {
	maxWhiteRatio = 0.82,
	sampleStep = 8,
	whiteThresh = 245,
} = {}) => new Promise((resolve) => {
	if (!blob) {
		resolve({ok: false, whiteRatio: 1});
		return;
	}
	const url = URL.createObjectURL(blob);
	const img = new Image();
	img.onload = () => {
		try {
			const w = img.naturalWidth || img.width;
			const h = img.naturalHeight || img.height;
			if (!(w > 4 && h > 4)) {
				URL.revokeObjectURL(url);
				resolve({ok: false, whiteRatio: 1});
				return;
			}
			const canvas = document.createElement("canvas");
			canvas.width = w;
			canvas.height = h;
			const ctx = canvas.getContext("2d", {willReadFrequently: true});
			ctx.drawImage(img, 0, 0);
			const data = ctx.getImageData(0, 0, w, h).data;
			let total = 0;
			let white = 0;
			const step = Math.max(1, sampleStep);
			for (let y = 0; y < h; y += step) {
				for (let x = 0; x < w; x += step) {
					const i = (y * w + x) * 4;
					const r = data[i];
					const g = data[i + 1];
					const b = data[i + 2];
					const a = data[i + 3];
					total += 1;
					if (a < 10 || (r >= whiteThresh && g >= whiteThresh && b >= whiteThresh)) {
						white += 1;
					}
				}
			}
			URL.revokeObjectURL(url);
			const whiteRatio = total ? white / total : 1;
			resolve({ok: whiteRatio < maxWhiteRatio, whiteRatio});
		} catch (e) {
			try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
			resolve({ok: true, whiteRatio: null}); // don't block on canvas errors
		}
	};
	img.onerror = () => {
		try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
		resolve({ok: true, whiteRatio: null});
	};
	img.src = url;
});

/**
 * Navigate to each view request, wait for tiles, capture screenshots.
 * Returns site objects suitable for sendChatMessageWithOptionalScreenshot.
 */
export const fulfillViewRequests = async ({
	slidemap,
	slide,
	displaySetting,
	activeMapId,
	requests,
	attachments,
	currentMapState,
	extension = ScreenshotExtensions.JPEG,
	onProgress,
	maxRequests = MAX_VIEW_REQUESTS_PER_ROUND,
	skipped = null,
	skipDuplicates = true,
} = {}) => {
	const reqs = (requests || []).slice(0, maxRequests);
	const sites = [];
	const prev = currentMapState || null;
	const slideBounds = readSlideBounds(slide, slidemap);
	const skip = (i, req, reason, extra = {}) => {
		onProgress?.({index: i, phase: "skip", reason, ...extra});
		if (Array.isArray(skipped)) skipped.push({request: req, reason, ...extra});
	};
	for (let i = 0; i < reqs.length; i++) {
		const req = reqs[i];
		let ms = resolveViewRequestMapState(req, attachments, {
			currentMapState: prev,
			slidemap,
			slide,
		});
		if (!ms) {
			skip(i, req, "unresolved");
			continue;
		}
		ms = clampMapStateToSlideBounds(ms, slideBounds)
			|| clampMapStateToSlideBounds(ms, attachments?.[0]?.slide_bounds || attachments?.[0]?.slideBounds)
			|| ms;
		if (skipDuplicates) {
			const dup = findDuplicateView(ms, [...(attachments || []), ...sites], {slidemap, slide});
			if (dup) {
				skip(i, req, "duplicate", {duplicateOf: dup});
				continue;
			}
		}
		onProgress?.({index: i, phase: "navigate", intent: req.intent, mapState: ms});
		const nav = await navigateToMapStateVerified(slidemap, ms);
		onProgress?.({index: i, phase: "capture"});
		const capture = await captureAiViewportScreenshot({
			slide,
			slidemap,
			activeMapId,
			mapState: nav.mapState || ms,
			displaySetting,
			extension,
		});
		if (!capture?.blob && !capture?.previewUrl) {
			skip(i, req, "capture_failed");
			continue;
		}
		if (capture.blob) {
			const tissue = await assessCaptureTissue(capture.blob);
			if (!tissue.ok) {
				skip(i, req, "mostly_empty", {whiteRatio: tissue.whiteRatio});
				if (capture.previewUrl) {
					try { URL.revokeObjectURL(capture.previewUrl); } catch (e) { /* ignore */ }
				}
				continue;
			}
		}
		const sc = capture.scaleContext || null;
		sites.push({
			label: null, // assigned by caller via nextChatSiteLabel
			blob: capture.blob || null,
			fileId: capture.fileId || null,
			previewUrl: capture.previewUrl || null,
			urlSafe: capture.urlSafe || null,
			mapState: capture.mapState || nav.mapState || ms,
			extent: capture.extent || null,
			imageSize: capture.imageSize || null,
			viewportCssSize: capture.viewportCssSize || null,
			canvasSize: capture.viewportCssSize || capture.imageSize || null,
			slideBounds: capture.slideBounds || slideBounds || null,
			scaleContext: sc,
			available_zooms: sc?.available_zooms || null,
			autoCapture: true,
			viewRequestId: req.id,
			viewIntent: req.intent,
			viewRequest: req,
			viewResolved: {
				sourceSite: ms.sourceSite,
				sourceZ: ms.sourceZ,
				z: ms.z,
				requestedZ: ms.requestedZ,
				zCapped: ms.zCapped,
				zooms: ms.zooms || sc?.available_zooms || null,
				arrived: nav.arrived,
				settled: nav.settled,
			},
		});
		onProgress?.({index: i, phase: "done"});
	}
	if (prev && slidemap?.getView?.()) {
		try {
			await animateToMapStateAsync(slidemap.getView(), prev, 200);
		} catch (e) { /* ignore */ }
	}
	return sites;
};

/**
 * Label of an earlier image showing the same view (same zoom, center within 15% of the
 * field), or null. Compares against attachments and sites captured this round.
 */
export const findDuplicateView = (ms, images = [], {slidemap = null, slide = null, centerFrac = 0.15} = {}) => {
	const view = slidemap?.getView?.();
	if (!view?.getResolutionForZoom || !ms) return null;
	const targetRes = view.getResolutionForZoom(Number(ms.z));
	if (!(targetRes > 0)) return null;
	for (const img of images || []) {
		const frame = buildCaptureFrame({
			mapState: img.map_state || img.mapState,
			canvasSize: img.canvas_size || img.canvasSize,
			viewportCssSize: img.viewport_css_size || img.viewportCssSize,
			imageSize: img.image_size || img.imageSize,
			extent: img.extent || null,
			slidemap,
			slide,
		});
		if (!frame) continue;
		if (Math.abs(frame.resolution / targetRes - 1) > 0.1) continue;
		const fieldPx = Math.min(frame.size[0], frame.size[1]);
		const dPx = Math.hypot(frame.center[0] - Number(ms.x), frame.center[1] - Number(ms.y)) / targetRes;
		if (dPx <= fieldPx * centerFrac) return img.label || "earlier image";
	}
	return null;
};

/** Promise-based view animate (for per-site annotation placement). */
export const animateToMapStateAsync = (view, mapState, duration = 300) => new Promise((resolve) => {
	if (!view || !mapState) {
		resolve(false);
		return;
	}
	const cur = view.getCenter?.() || [0, 0];
	const center = [
		mapState.x != null && mapState.x !== -1 ? Number(mapState.x) : cur[0],
		mapState.y != null && mapState.y !== -1 ? Number(mapState.y) : cur[1],
	];
	view.animate(
		{
			center,
			zoom: mapState.z != null ? Number(mapState.z) : view.getZoom(),
			rotation: mapState.r != null ? Number(mapState.r) : view.getRotation(),
			duration,
		},
		(complete) => resolve(complete !== false),
	);
});

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Convert one AI feature's canvas pixels → slide coords.
 * Prefers CaptureFrame (rotation-aware, capture-size stable). Falls back to live
 * getCoordinateFromPixel only when frame cannot be built.
 */
export const convertAiFeatureViaMap = (feature, slidemap, siteContext = {}, {slide = null} = {}) => {
	if (!feature?.geometry) return null;
	const frame = buildCaptureFrame({
		mapState: siteContext.mapState,
		canvasSize: siteContext.canvasSize,
		viewportCssSize: siteContext.viewportCssSize,
		imageSize: siteContext.imageSize,
		extent: siteContext.extent || null,
		slidemap,
		slide,
	});
	if (frame) return convertAiFeatureViaFrame(feature, frame);

	// Legacy fallback: live map must already be at capture map_state.
	if (!slidemap?.getCoordinateFromPixel) return null;
	const view = slidemap.getView?.();
	if (!view) return null;
	const mapSize = slidemap.getSize?.();
	if (!mapSize || mapSize.length < 2 || !(mapSize[0] > 0) || !(mapSize[1] > 0)) return null;

	const cap = siteContext.viewportCssSize
		|| siteContext.imageSize
		|| siteContext.canvasSize
		|| mapSize;
	const capW = Number(cap[0]) || mapSize[0];
	const capH = Number(cap[1]) || mapSize[1];
	const sx = mapSize[0] / capW;
	const sy = mapSize[1] / capH;
	const res = view.getResolution() || 1;

	const mapPair = (c) => {
		if (!Array.isArray(c) || c.length < 2) return null;
		const px = Number(c[0]) * sx;
		const py = Number(c[1]) * sy;
		if (![px, py].every(Number.isFinite)) return null;
		const coord = slidemap.getCoordinateFromPixel([px, py]);
		if (!coord || !Number.isFinite(coord[0]) || !Number.isFinite(coord[1])) return null;
		return [coord[0], coord[1]];
	};
	const mapLine = (line) => {
		if (!Array.isArray(line)) return null;
		const out = [];
		for (const c of line) {
			const p = mapPair(c);
			if (!p) return null;
			out.push(p);
		}
		return out;
	};

	const geom = feature.geometry;
	let coordinates;
	if (geom.type === "Point") {
		coordinates = mapPair(geom.coordinates);
	} else if (geom.type === "LineString") {
		coordinates = mapLine(geom.coordinates);
	} else if (geom.type === "Polygon") {
		const rings = [];
		for (const ring of geom.coordinates || []) {
			const mapped = mapLine(ring);
			if (!mapped) return null;
			rings.push(mapped);
		}
		coordinates = rings;
	} else if (geom.type === "MultiPoint") {
		const pts = [];
		for (const c of geom.coordinates || []) {
			const p = mapPair(c);
			if (!p) return null;
			pts.push(p);
		}
		coordinates = pts;
	} else {
		return null;
	}
	if (!coordinates) return null;

	const props = {...(feature.properties || {}), coordinate_space: "slide"};
	if (props.radius != null && Number.isFinite(Number(props.radius))) {
		props.radius = Number(props.radius) * ((sx + sy) / 2) * res;
	}
	return {
		...feature,
		geometry: {...geom, coordinates},
		properties: props,
	};
};

/**
 * Animate to each capture site, convert canvas_pixels via getCoordinateFromPixel, add annotations.
 * Restores the previous view when done.
 */
export const placeAiFeaturesOnSlide = async ({
	slidemap,
	slide = null,
	featureItems,
	attachments = [],
	slideId,
	creatorId,
	annoDrawer,
	defaultZ,
	uperpixel = 1,
	onProgress,
	addPayload,
	restoreView = true,
	resolveAttachmentSrc = null,
}) => {
	if (!slidemap || !slideId || !featureItems?.length) {
		return {saved: 0, placedKeys: [], failures: []};
	}
	const failures = [];
	const known = new Set((attachments || []).map((a) => String(a?.label || "").toUpperCase()).filter(Boolean));
	const bySite = new Map();
	featureItems.forEach((item) => {
		const props = item.feature?.properties || {};
		const named = String(props.source_site || props.site || item.sourceSite || "").toUpperCase();
		if (named && known.size && !known.has(named)) {
			failures.push({key: item.key, label: item.label, reason: "unknown_image", site: named});
			return;
		}
		const ctx = resolveAiSiteContext(item.feature, attachments, item.collectionMeta || {});
		const siteKey = (ctx.label || item.sourceSite || "_").toString().toUpperCase();
		if (!bySite.has(siteKey)) bySite.set(siteKey, {ctx, items: []});
		bySite.get(siteKey).items.push(item);
	});

	const view = slidemap.getView();
	const prevCenter = view.getCenter();
	const prev = {
		x: prevCenter?.[0],
		y: prevCenter?.[1],
		z: view.getZoom(),
		r: view.getRotation(),
	};

	let saved = 0;
	const placedKeys = [];
	try {
		for (const [siteKey, group] of bySite.entries()) {
			const att = (attachments || []).find(
				(a) => String(a.label || "").toUpperCase() === siteKey
			) || (attachments || [])[0];
			// Older images were saved without size metadata: read it from the image itself.
			if (!(group.ctx.canvasSize || group.ctx.imageSize) && att && typeof resolveAttachmentSrc === "function") {
				try {
					const size = await _imageSrcSize(await resolveAttachmentSrc(att));
					if (size) group.ctx = {...group.ctx, imageSize: size, canvasSize: size};
				} catch (e) { /* reported below */ }
			}
			const frame = buildCaptureFrame({
				mapState: group.ctx.mapState,
				canvasSize: group.ctx.canvasSize,
				viewportCssSize: group.ctx.viewportCssSize,
				imageSize: group.ctx.imageSize,
				extent: group.ctx.extent || null,
				slidemap,
				slide,
			});
			if (!frame) {
				const reason = !(group.ctx.canvasSize || group.ctx.imageSize) ? "no_image_size" : "no_position";
				group.items.forEach((item) => failures.push({key: item.key, label: item.label, reason, site: siteKey}));
				continue;
			}
			group.ctx = {...group.ctx, mapState: {...(group.ctx.mapState || {}), ...frame.mapState}};
			const ms = group.ctx.mapState;
			onProgress?.({site: siteKey, phase: "navigate"});
			// Navigate for UX; conversion uses CaptureFrame and does not depend on live size.
			const navTarget = group.items.length === 1
				? mapStateForAiFeature(group.items[0], attachments, {slidemap, slide, currentMapState: ms})
				: ms;
			if (navTarget && (navTarget.x != null || navTarget.y != null || navTarget.z != null)) {
				await animateToMapStateAsync(view, navTarget, 280);
				await _sleep(120);
			}
			// Replace rough whole-fragment outlines with the traced tissue contour.
			let snapped = group.items.map((item) => item.feature);
			if (typeof resolveAttachmentSrc === "function") {
				try {
					const src = att ? await resolveAttachmentSrc(att) : null;
					const raster = await getTissueRaster(src, group.ctx.canvasSize);
					snapped = snapFeaturesToTissue(snapped, raster);
				} catch (e) {
					// Snap is best-effort; fall back to the AI geometry.
				}
			}
			for (const [idx, item] of group.items.entries()) {
				const slideFeat = convertAiFeatureViaFrame(snapped[idx], frame);
				if (!slideFeat) {
					failures.push({key: item.key, label: item.label, reason: "bad_geometry", site: siteKey});
					continue;
				}
				const zLevel = Number.isFinite(Number(navTarget?.z))
					? Number(navTarget.z)
					: (Number.isFinite(Number(ms?.z)) ? Number(ms.z) : defaultZ);
				const payload = annotationPayloadFromAiFeature({
					feature: slideFeat,
					slideId,
					creatorId,
					annoDrawer,
					zLevel,
					uperpixel,
					siteContext: group.ctx,
					collectionMeta: {coordinate_space: "slide"},
				});
				if (payload && typeof addPayload === "function") {
					addPayload(payload);
					saved += 1;
					if (item.key) placedKeys.push(item.key);
					onProgress?.({site: siteKey, phase: "placed", key: item.key, index: item.index});
				} else if (!payload) {
					failures.push({key: item.key, label: item.label, reason: "bad_geometry", site: siteKey});
				}
			}
		}
	} finally {
		if (restoreView && prev.x != null) {
			await animateToMapStateAsync(view, prev, 200);
		}
	}
	return {saved, placedKeys, failures};
};

/** Natural [w, h] of an image URL / object URL (null on failure). */
const _imageSrcSize = (src) => new Promise((resolve) => {
	if (!src) {
		resolve(null);
		return;
	}
	const img = new Image();
	img.onload = () => resolve(img.naturalWidth > 0 ? [img.naturalWidth, img.naturalHeight] : null);
	img.onerror = () => resolve(null);
	img.src = src;
});

/** Open gamma viewer at a map_state (x/y/z/r). */
export const gammaUrlForMapState = (slideId, mapState, {openChat = false, chatId = null} = {}) => {
	if (!slideId) return null;
	const ms = mapState || {};
	const params = new URLSearchParams();
	params.set("slideId", String(slideId));
	if (ms.x != null && ms.x !== -1) params.set("x", String(ms.x));
	if (ms.y != null && ms.y !== -1) params.set("y", String(ms.y));
	if (ms.z != null) params.set("z", String(ms.z));
	if (ms.r != null) params.set("r", String(ms.r));
	if (openChat) params.set("morphoChat", "1");
	if (chatId) params.set("chat_id", String(chatId));
	return `/gamma/${slideId}/?${params.toString()}`;
};

const ringArea = (ring) => {
	let a = 0;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		a += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
	}
	return Math.abs(a / 2);
};

const ringPerimeter = (ring) => {
	let p = 0;
	for (let i = 1; i < ring.length; i++) {
		const dx = ring[i][0] - ring[i - 1][0];
		const dy = ring[i][1] - ring[i - 1][1];
		p += Math.sqrt(dx * dx + dy * dy);
	}
	return p;
};

const ringCenter = (ring) => {
	let sx = 0;
	let sy = 0;
	const n = Math.max(1, ring.length - (ring.length > 1 ? 1 : 0));
	const limit = ring.length > 1 ? ring.length - 1 : ring.length;
	for (let i = 0; i < limit; i++) {
		sx += ring[i][0];
		sy += ring[i][1];
	}
	return [sx / n, sy / n];
};

const _aiAnnoColor = (props) => {
	const style = dermFeatureStyle(props?.class || props?.feature_class || props?.label || props?.name, props?.color);
	return style.color || _DEFAULT_FEAT_COLOR;
};

/**
 * Build a CaptureFrame for rotation-aware canvas↔slide transforms.
 * Matches OpenLayers Map renderer matrices (ol/renderer/Map.js calculateMatrices2D).
 * Uses capture map_state + capture canvas size — independent of the live map size
 * (so Morpho Chat drawer resize does not shift annotations).
 */
export const buildCaptureFrame = ({
	mapState,
	canvasSize,
	viewportCssSize,
	imageSize,
	extent = null,
	slidemap,
	slide,
} = {}) => {
	const ms = mapState || {};
	const size = viewportCssSize || canvasSize || imageSize;
	if (!size || size.length < 2) return null;
	const W = Number(size[0]);
	const H = Number(size[1]);
	let cx = Number(ms.x);
	let cy = Number(ms.y);
	const rotation = Number(ms.r) || 0;
	const z = ms.z != null && Number.isFinite(Number(ms.z)) ? Number(ms.z) : null;

	let resolution = Number(ms.resolution) > 0 ? Number(ms.resolution) : null;
	// Older captures stored the requested map_state, which can differ from what was on
	// screen; the extent was read from the live view together with the bitmap.
	const ext = Array.isArray(extent) && extent.length >= 4 ? extent.map(Number) : null;
	if (!resolution && ext && ext.every(Number.isFinite) && W > 0 && H > 0) {
		const c = Math.abs(Math.cos(rotation));
		const s = Math.abs(Math.sin(rotation));
		const resX = (ext[2] - ext[0]) / (W * c + H * s);
		const resY = (ext[3] - ext[1]) / (W * s + H * c);
		if (resX > 0 && resY > 0 && Math.abs(resX / resY - 1) < 0.05) {
			resolution = (resX + resY) / 2;
			cx = (ext[0] + ext[2]) / 2;
			cy = (ext[1] + ext[3]) / 2;
		}
	}
	if (!(W > 0 && H > 0 && Number.isFinite(cx) && Number.isFinite(cy))) return null;
	if (!resolution) {
		try {
			const view = slidemap?.getView?.();
			if (view?.getResolutionForZoom && z != null) {
				resolution = view.getResolutionForZoom(z);
			} else if (view?.getResolution && z == null) {
				resolution = view.getResolution();
			}
		} catch (e) { /* ignore */ }
	}
	if (!Number.isFinite(resolution) || !(resolution > 0)) {
		const levels = getZoomLevels(slide);
		const resList = levels.length ? getResolutions(levels) : [];
		if (z != null && resList[z] != null) resolution = Number(resList[z]);
	}
	if (!Number.isFinite(resolution) || !(resolution > 0)) return null;

	const coordinateToPixel = createTransform();
	composeTransform(
		coordinateToPixel,
		W / 2,
		H / 2,
		1 / resolution,
		-1 / resolution,
		-rotation,
		-cx,
		-cy,
	);
	const pixelToCoordinate = createTransform();
	makeInverse(pixelToCoordinate, coordinateToPixel);

	return {
		size: [W, H],
		center: [cx, cy],
		rotation,
		resolution,
		z,
		mapState: {x: cx, y: cy, z, r: rotation},
		coordinateToPixel,
		pixelToCoordinate,
	};
};

/** Canvas/CSS pixel (top-left origin) → slide coordinate via CaptureFrame. */
export const canvasPixelToSlide = (frame, px, py) => {
	if (!frame?.pixelToCoordinate) return null;
	const out = applyTransform(frame.pixelToCoordinate, [Number(px), Number(py)]);
	if (!out || !Number.isFinite(out[0]) || !Number.isFinite(out[1])) return null;
	return [out[0], out[1]];
};

/** Length in canvas pixels → slide units. */
export const canvasLengthToSlide = (frame, lenPx) => {
	if (!frame || !Number.isFinite(frame.resolution)) return Number(lenPx) || 0;
	return Number(lenPx) * frame.resolution;
};

/** Flatten geometry coordinates to [x,y,x,y,...] canvas pixels. */
export const collectFeatureCanvasCoords = (feature) => {
	const geom = feature?.geometry;
	if (!geom) return [];
	const flat = [];
	const push = (c) => {
		if (Array.isArray(c) && c.length >= 2 && !Array.isArray(c[0])) {
			flat.push(Number(c[0]), Number(c[1]));
		}
	};
	if (geom.type === "Point") push(geom.coordinates);
	else if (geom.type === "MultiPoint" || geom.type === "LineString") {
		(geom.coordinates || []).forEach(push);
	} else if (geom.type === "Polygon") {
		(geom.coordinates || []).forEach((ring) => (ring || []).forEach(push));
	} else if (geom.type === "MultiLineString" || geom.type === "MultiPolygon") {
		// uncommon for Morpho Chat; best-effort flatten one level
		(geom.coordinates || []).forEach((part) => {
			if (!Array.isArray(part)) return;
			if (Array.isArray(part[0]) && !Array.isArray(part[0][0])) part.forEach(push);
			else part.forEach((ring) => (ring || []).forEach(push));
		});
	}
	return flat.filter((n) => Number.isFinite(n));
};

export const featureCanvasBBox = (feature) => {
	const flat = collectFeatureCanvasCoords(feature);
	if (flat.length < 2) return null;
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (let i = 0; i < flat.length; i += 2) {
		minX = Math.min(minX, flat[i]);
		maxX = Math.max(maxX, flat[i]);
		minY = Math.min(minY, flat[i + 1]);
		maxY = Math.max(maxY, flat[i + 1]);
	}
	if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
	return {minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY};
};

export const featureCanvasCentroid = (feature) => {
	const bbox = featureCanvasBBox(feature);
	if (!bbox) return null;
	return [(bbox.minX + bbox.maxX) / 2, (bbox.minY + bbox.maxY) / 2];
};

/** True when polygon roughly fills the whole screenshot (bad FOV slab). */
export const isAiFeatureSuspectFovFill = (feature, canvasSize) => {
	const bbox = featureCanvasBBox(feature);
	if (!bbox || !canvasSize || canvasSize.length < 2) return false;
	const W = Number(canvasSize[0]);
	const H = Number(canvasSize[1]);
	if (!(W > 0 && H > 0)) return false;
	return (bbox.width / W) > 0.85 && (bbox.height / H) > 0.85;
};

/**
 * Fit zoom so bbox fills ~fillFrac of the viewport (closer_look Go to / auto FOV).
 * Returns integer z clamped to [captureZ+1, zMax] when stepping up.
 */
export const fitZoomForCanvasBBox = (bbox, canvasSize, captureZ, {
	zMax = 20,
	fillFrac = 0.6,
	minStep = 1,
} = {}) => {
	const baseZ = Number.isFinite(Number(captureZ)) ? Number(captureZ) : 0;
	if (!bbox || !canvasSize) {
		return Math.min(zMax, baseZ + Math.max(1, minStep));
	}
	const W = Number(canvasSize[0]);
	const H = Number(canvasSize[1]);
	const bw = Math.max(1, bbox.width);
	const bh = Math.max(1, bbox.height);
	if (!(W > 0 && H > 0)) return Math.min(zMax, baseZ + minStep);
	const scale = Math.min(W / bw, H / bh) * fillFrac;
	const steps = Number.isFinite(scale) && scale > 1
		? Math.floor(Math.log2(scale))
		: minStep;
	const z = baseZ + Math.max(minStep, steps);
	return Math.max(0, Math.min(zMax, z));
};

export const isCloserLookFeature = (itemOrFeature) => {
	const props = itemOrFeature?.feature?.properties || itemOrFeature?.properties || itemOrFeature || {};
	const label = String(props.label || props.name || itemOrFeature?.label || "").toLowerCase();
	const cls = String(props.class || props.feature_class || itemOrFeature?.classKey || "").toLowerCase();
	return (
		cls === "closer_look" || cls === "roi"
		|| label.startsWith("closer_look") || label.startsWith("roi")
		|| label.includes("closer_look")
	);
};

/**
 * map_state for Go to / auto FOV for one AI feature (region + mag for closer_look).
 */
export const mapStateForAiFeature = (item, attachments = [], {
	slidemap,
	slide,
	currentMapState = null,
} = {}) => {
	const ctx = resolveAiSiteContext(
		item?.feature || item,
		attachments,
		item?.collectionMeta || {},
	);
	const baseMs = ctx.mapState || currentMapState || {};
	const canvas = ctx.canvasSize || ctx.viewportCssSize || ctx.imageSize;
	const frame = buildCaptureFrame({
		mapState: baseMs,
		canvasSize: canvas,
		viewportCssSize: ctx.viewportCssSize,
		imageSize: ctx.imageSize,
		extent: ctx.mapState ? ctx.extent : null,
		slidemap,
		slide,
	});
	const centroid = featureCanvasCentroid(item?.feature || item);
	let x = baseMs.x;
	let y = baseMs.y;
	if (centroid && frame) {
		const slidePt = canvasPixelToSlide(frame, centroid[0], centroid[1]);
		if (slidePt) {
			x = slidePt[0];
			y = slidePt[1];
		}
	}
	const zooms = (attachments || []).flatMap((a) => a?.available_zooms || a?.availableZooms || []);
	const zMax = zooms.length
		? Math.max(...zooms.map((e) => (e?.z != null ? Number(e.z) : -1)).filter((n) => n >= 0), 20)
		: (getZoomLevels(slide).length ? getZoomLevels(slide).length - 1 : 20);
	const props = item?.feature?.properties || {};
	let z = baseMs.z != null ? Number(baseMs.z) : 0;
	const targetZ = props.target_z != null ? Number(props.target_z) : null;
	const targetMpp = props.target_mpp_um_per_px != null ? Number(props.target_mpp_um_per_px) : null;
	const targetMag = _magNum(props.target_mag ?? props.target_magnification ?? props.magnification);
	if (Number.isFinite(targetZ)) {
		z = Math.max(0, Math.min(zMax, Math.round(targetZ)));
	} else if (Number.isFinite(targetMag) && zooms.some((e) => e?.optical_x > 0)) {
		let best = z;
		let bestDiff = Infinity;
		zooms.forEach((entry) => {
			if (!(entry?.optical_x > 0)) return;
			const d = Math.abs(Math.log(entry.optical_x / targetMag));
			if (d < bestDiff) {
				bestDiff = d;
				best = Number(entry.z);
			}
		});
		z = Math.max(0, Math.min(zMax, best));
	} else if (Number.isFinite(targetMpp) && zooms.length) {
		let best = z;
		let bestDiff = Infinity;
		zooms.forEach((entry, i) => {
			const mpp = Number(entry?.mpp_um_per_px != null ? entry.mpp_um_per_px : entry);
			if (!Number.isFinite(mpp)) return;
			const d = Math.abs(mpp - targetMpp);
			if (d < bestDiff) {
				bestDiff = d;
				best = entry?.z != null ? Number(entry.z) : i;
			}
		});
		z = best;
	} else if (isCloserLookFeature(item)) {
		const bbox = featureCanvasBBox(item?.feature || item);
		z = fitZoomForCanvasBBox(bbox, canvas || frame?.size, baseMs.z, {zMax, fillFrac: 0.6, minStep: 1});
	}
	if (![x, y].every(Number.isFinite)) return null;
	return {
		x,
		y,
		z,
		r: baseMs.r != null ? Number(baseMs.r) : 0,
		labelHint: item?.label || props.label,
		sourceSite: ctx.label || item?.sourceSite || null,
	};
};

/**
 * Convert AI feature canvas_pixels → slide via CaptureFrame (rotation-aware).
 */
export const convertAiFeatureViaFrame = (feature, frame) => {
	if (!feature?.geometry || !frame) return null;
	const geom = feature.geometry;
	const mapPair = (c) => {
		if (!Array.isArray(c) || c.length < 2) return null;
		return canvasPixelToSlide(frame, c[0], c[1]);
	};
	const mapLine = (line) => {
		if (!Array.isArray(line)) return null;
		const out = [];
		for (const c of line) {
			const p = mapPair(c);
			if (!p) return null;
			out.push(p);
		}
		return out;
	};
	let coordinates;
	if (geom.type === "Point") coordinates = mapPair(geom.coordinates);
	else if (geom.type === "LineString") coordinates = mapLine(geom.coordinates);
	else if (geom.type === "Polygon") {
		const rings = [];
		for (const ring of geom.coordinates || []) {
			const mapped = mapLine(ring);
			if (!mapped) return null;
			rings.push(mapped);
		}
		coordinates = rings;
	} else if (geom.type === "MultiPoint") {
		const pts = [];
		for (const c of geom.coordinates || []) {
			const p = mapPair(c);
			if (!p) return null;
			pts.push(p);
		}
		coordinates = pts;
	} else return null;
	if (!coordinates) return null;
	const props = {...(feature.properties || {}), coordinate_space: "slide"};
	if (props.radius != null && Number.isFinite(Number(props.radius))) {
		props.radius = canvasLengthToSlide(frame, Number(props.radius));
	}
	return {...feature, geometry: {...geom, coordinates}, properties: props};
};

/**
 * Screenshot pixel (top-left origin) → slide/viewer coordinate.
 * Axis-aligned extent fallback only — prefer canvasPixelToSlide / CaptureFrame.
 */
export const imagePixelToSlide = (px, py, {extent, imageSize} = {}) => {
	const ext = extent;
	const size = imageSize;
	if (!ext || ext.length < 4 || !size || size.length < 2) return null;
	const [minX, minY, maxX, maxY] = ext.map(Number);
	const [imgW, imgH] = size.map(Number);
	if (![minX, minY, maxX, maxY, imgW, imgH].every(Number.isFinite) || imgW <= 0 || imgH <= 0) {
		return null;
	}
	const x = minX + (Number(px) / imgW) * (maxX - minX);
	// Image y↓ → viewer y↑ (OpenLayers extent).
	const y = maxY - (Number(py) / imgH) * (maxY - minY);
	return [x, y];
};

/** Scale a length in screenshot pixels → slide units (avg of X/Y scale). */
export const imageLengthToSlide = (len, {extent, imageSize} = {}) => {
	const ext = extent;
	const size = imageSize;
	if (!ext || ext.length < 4 || !size || size.length < 2) return Number(len) || 0;
	const spanX = Math.abs(Number(ext[2]) - Number(ext[0]));
	const spanY = Math.abs(Number(ext[3]) - Number(ext[1]));
	const imgW = Number(size[0]);
	const imgH = Number(size[1]);
	if (!(imgW > 0) || !(imgH > 0)) return Number(len) || 0;
	const scale = ((spanX / imgW) + (spanY / imgH)) / 2;
	return Number(len) * scale;
};

const _looksLikeImagePixels = (coordsFlat, imageSize) => {
	if (!imageSize || imageSize.length < 2 || !coordsFlat.length) return false;
	const [imgW, imgH] = imageSize.map(Number);
	if (!(imgW > 0 && imgH > 0)) return false;
	let maxX = -Infinity;
	let maxY = -Infinity;
	let minX = Infinity;
	let minY = Infinity;
	for (let i = 0; i < coordsFlat.length; i += 2) {
		const x = Number(coordsFlat[i]);
		const y = Number(coordsFlat[i + 1]);
		if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
		maxX = Math.max(maxX, x);
		maxY = Math.max(maxY, y);
		minX = Math.min(minX, x);
		minY = Math.min(minY, y);
	}
	return minX >= -2 && minY >= -2 && maxX <= imgW + 2 && maxY <= imgH + 2;
};

/**
 * Resolve site context (extent + image_size) for a feature from message attachments.
 */
export const resolveAiSiteContext = (feature, attachments = [], collectionMeta = {}) => {
	const props = feature?.properties || {};
	const site = String(props.source_site || props.site || "").toUpperCase();
	const list = attachments || [];
	const att = (site && list.find((a) => String(a.label || "").toUpperCase() === site))
		|| list[0]
		|| null;
	const imageSize = att?.image_size || att?.imageSize || collectionMeta.image_size || collectionMeta.imageSize || null;
	const viewportCssSize = att?.viewport_css_size || att?.viewportCssSize || null;
	const canvasSize = att?.canvas_size || att?.canvasSize
		|| collectionMeta.canvas_size || collectionMeta.canvasSize
		|| viewportCssSize || imageSize || null;
	return {
		extent: att?.extent || collectionMeta.extent || null,
		imageSize: imageSize || viewportCssSize || null,
		viewportCssSize,
		canvasSize,
		mapState: att?.map_state || att?.mapState || null,
		label: att?.label || site || null,
	};
};

/**
 * Convert feature coordinates from image_pixels → slide when needed.
 * Mutates a shallow-cloned feature; returns slide-space feature.
 */
export const normalizeAiFeatureToSlide = (feature, siteContext, collectionMeta = {}) => {
	if (!feature?.geometry) return feature;
	const props = feature.properties || {};
	const space = String(
		props.coordinate_space
		|| collectionMeta.coordinate_space
		|| collectionMeta.coordinateSpace
		|| ""
	).toLowerCase();
	const ctx = siteContext || {};
	const geom = feature.geometry;
	const collectFlat = [];
	const walk = (coords) => {
		if (!Array.isArray(coords)) return;
		if (typeof coords[0] === "number") {
			collectFlat.push(coords[0], coords[1]);
			return;
		}
		coords.forEach(walk);
	};
	walk(geom.coordinates);

	const forcePixels = space === "image_pixels" || space === "canvas_pixels"
		|| space === "pixel" || space === "pixels" || space === "image" || space === "canvas";
	const forceSlide = space === "slide" || space === "viewer" || space === "slide_viewer";
	// If bitmap was DPR-scaled but model traced CSS-sized coords (or vice versa),
	// pick the size that better contains the vertices.
	let convSize = ctx.imageSize;
	const cssSize = ctx.viewportCssSize;
	if (cssSize && ctx.imageSize && collectFlat.length) {
		const fitsImg = _looksLikeImagePixels(collectFlat, ctx.imageSize);
		const fitsCss = _looksLikeImagePixels(collectFlat, cssSize);
		if (fitsCss && !fitsImg) {
			convSize = cssSize;
		} else if (fitsCss && fitsImg) {
			const [iw, ih] = ctx.imageSize.map(Number);
			const [cw, ch] = cssSize.map(Number);
			// Prefer the smaller size when both fit (avoids treating CSS coords as hi-DPI).
			if (cw > 0 && ch > 0 && iw > 0 && ih > 0 && (iw * ih) > (cw * ch) * 1.2) {
				convSize = cssSize;
			}
		}
	}
	const looksImg = convSize && _looksLikeImagePixels(collectFlat, convSize);
	// Labeled image_pixels but vertices are clearly not in bitmap space (often slide coords
	// mis-tagged) — do not convert; leave as slide and avoid a large FOV shift.
	if (forcePixels && convSize && !looksImg && !(cssSize && _looksLikeImagePixels(collectFlat, cssSize))) {
		return feature;
	}
	const shouldConvert = forcePixels
		|| (!forceSlide && looksImg);

	if (!shouldConvert || !ctx.extent || !convSize) {
		return feature;
	}

	const convCtx = {...ctx, imageSize: convSize};
	const mapPair = (c) => {
		const slide = imagePixelToSlide(c[0], c[1], convCtx);
		return slide || [Number(c[0]), Number(c[1])];
	};
	const mapLine = (line) => line.map(mapPair);
	let coordinates;
	if (geom.type === "Point") {
		coordinates = mapPair(geom.coordinates);
	} else if (geom.type === "LineString") {
		coordinates = mapLine(geom.coordinates);
	} else if (geom.type === "Polygon") {
		coordinates = geom.coordinates.map(mapLine);
	} else if (geom.type === "MultiPoint") {
		coordinates = geom.coordinates.map(mapPair);
	} else {
		return feature;
	}

	const nextProps = {...props, coordinate_space: "slide"};
	if (props.radius != null && (forcePixels || shouldConvert)) {
		nextProps.radius = imageLengthToSlide(props.radius, convCtx);
	}
	return {
		...feature,
		geometry: {...geom, coordinates},
		properties: nextProps,
	};
};

const _circlePayload = ({
	x, y, radius, label, color, slideId, creatorId, annoDrawer, zLevel, uperpixel, props,
}) => {
	const r = Math.max(2, Number(radius) || 20);
	const cx = Number(x);
	const cy = Number(y);
	if (![cx, cy, r].every(Number.isFinite)) return null;
	return {
		area: Math.PI * r * r * Math.pow(uperpixel, 2),
		perimeter: 2 * Math.PI * r * uperpixel,
		color,
		new_bounds: JSON.stringify([[cx, cy], r]),
		slide: slideId,
		creator: creatorId,
		shape: "circle",
		tool_type: "Circle",
		center: `${cx},${cy}`,
		anno_drawer: annoDrawer,
		created_z_level: zLevel,
		title: label,
		meta: {
			source: "slide_ai",
			source_site: props?.source_site || null,
			width: 2,
		},
	};
};

const _lineLength = (coords) => {
	let p = 0;
	for (let i = 1; i < coords.length; i++) {
		const dx = coords[i][0] - coords[i - 1][0];
		const dy = coords[i][1] - coords[i - 1][1];
		p += Math.sqrt(dx * dx + dy * dy);
	}
	return p;
};

/**
 * Convert AI GeoJSON Feature → Morphle annotation payload (viewer/slide coords).
 * Tools: Circle, Freehand (free loop), Freeline, Polygon, Label.
 */
export const annotationPayloadFromAiFeature = ({
	feature,
	slideId,
	creatorId,
	annoDrawer,
	zLevel,
	uperpixel = 1,
	siteContext = null,
	collectionMeta = null,
}) => {
	if (!feature?.geometry || !slideId) return null;
	const normalized = normalizeAiFeatureToSlide(
		feature,
		siteContext || resolveAiSiteContext(feature, [], collectionMeta || {}),
		collectionMeta || {},
	);
	const geom = normalized.geometry;
	const props = normalized.properties || {};
	const label = (props.label || props.name || "AI feature").toString().slice(0, 64);
	const color = _aiAnnoColor(props);
	const tool = String(props.tool || props.tool_type || "").toLowerCase();

	if (geom.type === "Point" && Array.isArray(geom.coordinates)) {
		const [x, y] = geom.coordinates.map(Number);
		const radius = props.radius != null ? Number(props.radius) : 20;
		return _circlePayload({
			x, y, radius, label, color, slideId, creatorId, annoDrawer, zLevel, uperpixel, props,
		});
	}

	if (geom.type === "LineString" && Array.isArray(geom.coordinates)) {
		let coords = geom.coordinates.map((c) => [Number(c[0]), Number(c[1])]);
		if (coords.length < 2) return null;
		// Arrow / Line are 2-point tools: keep tail → tip (arrowhead is drawn at the last point).
		if (tool === "arrow" || tool === "line") coords = [coords[0], coords[coords.length - 1]];
		const lineToolType = tool === "arrow" ? "Arrow" : (tool === "line" ? "Line" : "Freeline");
		const peri = _lineLength(coords);
		const [cx, cy] = ringCenter(coords);
		return {
			area: 0,
			perimeter: peri * uperpixel,
			color,
			new_bounds: JSON.stringify(coords),
			slide: slideId,
			creator: creatorId,
			shape: "line",
			tool_type: lineToolType,
			center: `${cx},${cy}`,
			anno_drawer: annoDrawer,
			created_z_level: zLevel,
			title: label,
			meta: {source: "slide_ai", source_site: props.source_site || null, width: 2},
		};
	}

	if (geom.type === "Polygon" && Array.isArray(geom.coordinates?.[0])) {
		let ring = geom.coordinates[0].map((c) => [Number(c[0]), Number(c[1])]);
		if (ring.length < 3) return null;
		const first = ring[0];
		const last = ring[ring.length - 1];
		if (first[0] !== last[0] || first[1] !== last[1]) {
			ring = [...ring, first];
		}
		const [cx, cy] = ringCenter(ring);
		const areaPx = ringArea(ring);
		const periPx = ringPerimeter(ring);

		if (tool === "circle") {
			const equivR = props.radius != null
				? Number(props.radius)
				: Math.sqrt(areaPx / Math.PI) || 20;
			return _circlePayload({
				x: cx, y: cy, radius: equivR, label, color,
				slideId, creatorId, annoDrawer, zLevel, uperpixel, props,
			});
		}
		if (tool === "label") {
			return {
				area: areaPx * Math.pow(uperpixel, 2),
				perimeter: periPx * uperpixel,
				color,
				new_bounds: JSON.stringify([ring]),
				slide: slideId,
				creator: creatorId,
				shape: "polygon",
				tool_type: "Label",
				center: `${cx},${cy}`,
				anno_drawer: annoDrawer,
				created_z_level: zLevel,
				title: label,
				meta: {source: "slide_ai", source_site: props.source_site || null},
			};
		}
		const toolType = tool === "polygon" ? "Polygon" : (tool === "rectangle" ? "Rectangle" : "Freehand");
		return {
			area: areaPx * Math.pow(uperpixel, 2),
			perimeter: periPx * uperpixel,
			color,
			new_bounds: JSON.stringify([ring]),
			slide: slideId,
			creator: creatorId,
			shape: "polygon",
			tool_type: toolType,
			center: `${cx},${cy}`,
			anno_drawer: annoDrawer,
			created_z_level: zLevel,
			title: label,
			meta: {source: "slide_ai", source_site: props.source_site || null, width: 2},
		};
	}
	return null;
};

/** Rewrite Image A / [site:A] mentions into markdown links for clickable chips. */
export const linkifySiteRefs = (content) => {
	let text = content || "";
	text = text.replace(/\[site:([A-Za-z]+)\]/g, (_, L) => `[Image ${L.toUpperCase()}](#site:${L.toUpperCase()})`);
	text = text.replace(/\[Image\s+([A-Za-z]+)\]\(#site:[A-Za-z]+\)/g, "\u0000Image $1\u0000");
	text = text.replace(/\bImage\s+([A-Z]{1,3})\b/g, (_, L) => `[Image ${L}](#site:${L})`);
	text = text.replace(/\u0000Image\s+([A-Za-z]+)\u0000/g, (_, L) => `[Image ${L.toUpperCase()}](#site:${L.toUpperCase()})`);
	return text;
};
